<template>
  <!-- 代理人設定 -->
  <div>
    <VCard class="pa-0 pt-4">
      <!-- 搜尋條件 -->
      <VRow class="ma-0">
        <VCol cols="12" md="12" :class="smAndDown ? 'pa-0' : ''">
          <TemplateSearch class="ma-0">
            <template #searchTitle>
              <div class="d-flex align-center">
                <VIcon icon="tabler-search" class="mr-2" />
                搜尋條件
              </div>
            </template>

            <template #search>
              <VRow class="ma-0 search-filters-row" dense>
                <VCol cols="12" sm="6" md="3">
                  <VSelect
                    class="search-filter-control search-filter-formkey"
                    v-model="searchFormKeySingle"
                    :items="searchFormKeyOptions"
                    item-title="title"
                    item-value="value"
                    placeholder="代理範圍"
                    prepend-inner-icon="tabler-file-text"
                    density="compact"
                    variant="outlined"
                    hide-details
                    clearable
                  />
                </VCol>
                <VCol cols="12" sm="6" md="3">
                  <AppInput
                    v-model="filters.assignee"
                    type="picker"
                    item-title="label"
                    item-value="value"
                    placeholder="被代理人"
                    :picker="assigneeUserPicker"
                    :queryAll="false"
                    density="compact"
                    clearable
                  />
                </VCol>
                <VCol cols="12" sm="6" md="3">
                  <AppInput
                    v-model="filters.proxy"
                    type="picker"
                    item-title="label"
                    item-value="value"
                    placeholder="代理人"
                    :picker="centerUserPicker"
                    :queryAll="false"
                    density="compact"
                    clearable
                  />
                </VCol>
                <VCol cols="12" sm="6" md="3">
                  <AppDateTimePicker
                    v-model="filters.date"
                    placeholder="生效日期"
                    prepend-inner-icon="tabler-calendar-event"
                    density="compact"
                    variant="outlined"
                    hide-details
                    clearable
                  />
                </VCol>
                <VCol cols="12" sm="6" md="3">
                  <VSelect
                    class="search-filter-control search-filter-status"
                    v-model="filters.status"
                    :items="statusOptions"
                    item-title="title"
                    item-value="value"
                    placeholder="狀態"
                    prepend-inner-icon="tabler-list-check"
                    density="compact"
                    variant="outlined"
                    hide-details
                    multiple
                    chips
                    closable-chips
                    clearable
                  >
                    <template #chip="{ item, props, index }">
                      <VChip
                        v-bind="props"
                        :key="`${item.value}-${index}`"
                        :color="statusColor(item.value)"
                        class="rounded-pill"
                        variant="flat"
                        size="small"
                        closable
                      >
                        {{ item.title }}
                      </VChip>
                    </template>
                  </VSelect>
                </VCol>
              </VRow>
            </template>
          </TemplateSearch>
        </VCol>
      </VRow>

      <!-- 切換：代理人設定 / 操作紀錄（搜尋條件下方）+ 操作區 -->
      <div class="px-4 py-2 d-flex align-center justify-space-between flex-wrap ga-2 bg-surface">
        <div class="d-flex align-center ga-2 flex-wrap">
          <div v-if="isAdmin" class="cancellation-toggle cancellation-toggle--two" role="tablist" aria-label="檢視切換">
            <div
              class="cancellation-toggle__slider"
              :style="{ transform: `translateX(${viewActiveIndex * 100}%)` }"
            />
            <button
              v-for="opt in viewOptions"
              :key="opt.value"
              type="button"
              role="tab"
              class="cancellation-toggle__btn"
              :class="{ 'is-active': activeTab === opt.value }"
              :aria-selected="activeTab === opt.value"
              @click="activeTab = opt.value"
            >
              <VIcon :icon="opt.icon" size="16" />
              <span>{{ opt.label }}</span>
            </button>
          </div>
          <template v-else>
            <VIcon icon="tabler-user-cog" size="22" class="text-primary" />
            <span class="text-h6 font-weight-bold list-section-title">代理人設定</span>
          </template>
        </div>

        <div class="d-flex align-center flex-wrap ga-2">
          <VBtn
            v-if="activeTab === 'setting'"
            size="default"
            class="rounded-pill"
            variant="tonal"
            color="primary"
            prepend-icon="tabler-plus"
            @click="openDialog('add')"
          >
            新增
          </VBtn>
          <template v-else>
            <VSelect
              class="search-filter-control"
              v-model="logFilters.action"
              :items="logActionOptions"
              item-title="title"
              item-value="value"
              placeholder="操作別"
              prepend-inner-icon="tabler-filter"
              density="compact"
              variant="outlined"
              hide-details
              clearable
              style="min-width: 160px"
            />
            <VBtn
              size="small" class="rounded-pill" variant="tonal" color="primary"
              prepend-icon="tabler-refresh" @click="fetchLogData"
            >
              重新整理
            </VBtn>
          </template>
        </div>
      </div>

      <!-- 代理人設定：卡片式清單 -->
      <div v-show="activeTab === 'setting'" class="proxy-list-wrap px-4 pb-4">
        <div v-if="loading && !displayItems.length" class="proxy-list-loading d-flex justify-center align-center py-12">
          <VProgressCircular indeterminate color="primary" size="40" />
        </div>

        <div v-else-if="!displayItems.length" class="proxy-list-empty text-center py-12">
          <VIcon icon="tabler-user-cog" size="56" class="text-disabled mb-3" />
          <div class="text-h6 text-medium-emphasis mb-1">查無代理人設定</div>
          <div class="text-body-2 text-disabled mb-4">可調整搜尋條件，或新增一筆設定</div>
          <VBtn color="primary" variant="tonal" size="default" class="rounded-pill" prepend-icon="tabler-plus" @click="openDialog('add')">
            新增
          </VBtn>
        </div>

        <div v-else class="proxy-card-list">
          <article
            v-for="item in displayItems"
            :key="item.ids?.join('-') || item.id"
            class="proxy-card"
            :class="`proxy-card--status-${String(item.status)}`"
          >
            <div class="proxy-card__accent" />

            <div class="proxy-card__main">
              <div class="proxy-card__top">
                <div class="proxy-card__status-row">
                  <VChip
                    :color="statusColor(item.status)"
                    class="rounded-pill proxy-card__status-chip"
                    variant="flat"
                    size="default"
                    :prepend-icon="statusIcon(item.status)"
                  >
                    {{ statusLabel(item.status) }}
                  </VChip>
                  <div class="proxy-card__period">
                    <VIcon icon="tabler-calendar-event" size="20" class="proxy-card__period-icon" />
                    <span>{{ convertToTaiwanYear(item.startDate, '-') }}</span>
                    <span class="proxy-card__period-sep">～</span>
                    <span>{{ convertToTaiwanYear(item.endDate, '-') }}</span>
                  </div>
                </div>
                <span
                  v-if="String(item.status) === '1' && item.enableTime"
                  class="proxy-card__meta-time"
                >
                  啟用 {{ rocDateTime(item.enableTime) }}
                </span>
                <span
                  v-else-if="String(item.status) === '2' && item.updateTime"
                  class="proxy-card__meta-time"
                >
                  結束 {{ rocDateTime(item.updateTime) }}
                </span>
                <span
                  v-if="String(item.syncStatus) === 'F'"
                  class="proxy-card__sync-fail"
                >
                  同步失敗，請再按啟用重試
                </span>
              </div>

              <div class="proxy-card__people">
                <div class="proxy-card__person">
                  <span class="proxy-card__person-role">被代理人</span>
                  <span class="proxy-card__person-name">{{ userLabel(item.assignee) }}</span>
                </div>
                <div class="proxy-card__arrow" aria-hidden="true">
                  <VIcon icon="tabler-arrow-narrow-right" size="24" />
                </div>
                <div class="proxy-card__person proxy-card__person--proxy">
                  <span class="proxy-card__person-role">代理人</span>
                  <span class="proxy-card__person-name">{{ userLabel(item.proxy) }}</span>
                </div>
              </div>

              <div class="proxy-card__scope">
                <div class="proxy-card__scope-label">
                  <VIcon icon="tabler-file-text" size="18" />
                  <span>代理範圍</span>
                  <span v-if="!isDefaultScope(item)" class="proxy-card__scope-count">
                    {{ itemFormKeys(item).length }} 項
                    <template v-if="itemScopeSystemGroups(item).length > 1">
                      ／{{ itemScopeSystemGroups(item).length }} 系統
                    </template>
                  </span>
                </div>
                <div class="proxy-card__scope-body">
                  <template v-if="isDefaultScope(item)">
                    <span class="proxy-scope-badge proxy-scope-badge--default">
                      <VIcon icon="tabler-world" size="20" />
                      不指定範圍
                    </span>
                  </template>
                  <template v-else>
                    <div
                      v-for="group in itemScopeSystemGroups(item)"
                      :key="`${item.id}-sys-${group.system}`"
                      class="proxy-scope-system"
                      :class="`proxy-scope-system--${group.system}`"
                    >
                      <div class="proxy-scope-system__head">
                        <span class="proxy-scope-system__name">{{ group.title }}</span>
                        <span class="proxy-scope-system__count">{{ group.items.length }}</span>
                      </div>
                      <div class="proxy-scope-system__tags">
                        <span
                          v-for="fk in group.items"
                          :key="`${item.id}-${fk}`"
                          class="proxy-scope-tag"
                          :title="formKeyTitle(fk)"
                        >
                          {{ formKeyTitle(fk) }}
                        </span>
                      </div>
                    </div>
                  </template>
                </div>
              </div>

              <div
                v-if="item.remark"
                class="proxy-card__remark"
              >
                <div class="proxy-card__remark-label">
                  <VIcon icon="tabler-notes" size="18" />
                  <span>備註</span>
                </div>
                <div class="proxy-card__remark-body">{{ item.remark }}</div>
              </div>

              <div class="proxy-card__footer">
                <div
                  v-if="item.createUserId || item.updateUserId"
                  class="proxy-card__audit"
                >
                  <div v-if="item.createUserId" class="proxy-card__audit-row">
                    <span class="proxy-card__audit-label">建立</span>
                    <div class="proxy-card__audit-meta">
                      <span class="proxy-card__audit-user">{{ userLabel(item.createUserId) }}</span>
                      <span
                        v-if="item.createTime"
                        class="proxy-card__audit-time"
                      >{{ rocDateTime(item.createTime) }}</span>
                    </div>
                  </div>
                  <div v-if="item.updateUserId" class="proxy-card__audit-row">
                    <span class="proxy-card__audit-label">更新</span>
                    <div class="proxy-card__audit-meta">
                      <span class="proxy-card__audit-user">{{ userLabel(item.updateUserId) }}</span>
                      <span
                        v-if="item.updateTime"
                        class="proxy-card__audit-time"
                      >{{ rocDateTime(item.updateTime) }}</span>
                    </div>
                  </div>
                </div>

                <div v-if="canOperate(item)" class="proxy-card__actions">
                  <VBtn
                    v-if="String(item.status) === '0' || String(item.status) === '1' || String(item.status) === 'P'"
                    size="default"
                    variant="tonal"
                    color="warning"
                    class="rounded-pill"
                    prepend-icon="tabler-edit"
                    @click="openDialog('edit', item)"
                  >
                    修改
                  </VBtn>
                  <VBtn
                    v-if="String(item.status) === '1'"
                    size="default"
                    variant="tonal"
                    color="secondary"
                    class="rounded-pill"
                    prepend-icon="tabler-circle-minus"
                    :loading="disablingId === String(item.id)"
                    :disabled="!!disablingId"
                    @click="disableBtn(item)"
                  >
                    停用
                  </VBtn>
                  <VBtn
                    v-if="String(item.status) === '0'"
                    size="default"
                    variant="tonal"
                    color="success"
                    class="rounded-pill"
                    prepend-icon="tabler-circle-check"
                    @click="enableBtn(item)"
                  >
                    啟用
                  </VBtn>
                  <VBtn
                    v-if="String(item.status) === '0' || String(item.status) === 'P'"
                    size="default"
                    variant="tonal"
                    color="error"
                    class="rounded-pill"
                    prepend-icon="tabler-trash"
                    @click="deleteBtn(item)"
                  >
                    刪除
                  </VBtn>
                </div>
              </div>
            </div>
          </article>

          <!-- 手機：無限往下捲 -->
          <div
            v-if="smAndDown && hasMoreProxyList"
            v-intersect="onMobileIntersect"
            class="text-center py-4"
          >
            <VProgressCircular
              v-if="isLoadingMore"
              indeterminate
              color="primary"
              size="28"
            />
          </div>
          <div
            v-else-if="smAndDown && displayItems.length > 0"
            class="text-center text-caption py-3"
          >
            已載入全部
          </div>
        </div>

        <div
          v-if="!smAndDown && (displayItems.length || totalData > 0)"
          class="proxy-list-pagination mt-3"
        >
          <TablePagination
            v-model:page="page"
            v-model:size="size"
            :items-per-page="size"
            :total-items="totalData"
          />
        </div>
      </div>

      <!-- 操作紀錄表格（僅 Administrator）-->
      <div v-if="isAdmin" v-show="activeTab === 'log'" class="mt-2">
          <VDataTableServer
            v-model:items-per-page="logSize"
            v-model:page="logPage"
            :items-length="logTotal"
            :headers="logHeaders"
            :items="logItems"
            :loading="logLoading"
            class="rich-data-table"
            hover
          >
            <template #item.action="{ item }">
              <VChip :color="actionColor(item.action)" variant="flat" size="small" class="rounded-pill">
                {{ actionLabel(item.action) }}
              </VChip>
            </template>
            <template #item.formKey="{ item }">
              <div class="log-scope">
                <template v-if="isLogDefaultScope(item.formKey)">
                  <span class="log-scope__default">不指定範圍</span>
                </template>
                <template v-else-if="logScopeMeta(item.formKey).truncated">
                  <span class="log-scope__summary">共 {{ logScopeMeta(item.formKey).count }} 項</span>
                  <span
                    v-if="logScopeMeta(item.formKey).hint"
                    class="log-scope__hint"
                  >{{ logScopeMeta(item.formKey).hint }}</span>
                </template>
                <template v-else>
                  <div class="log-scope__summary">
                    共 {{ logScopeKeys(item.formKey).length }} 項
                    <template v-if="logScopeGroups(item.formKey).length > 1">
                      ／{{ logScopeGroups(item.formKey).length }} 系統
                    </template>
                  </div>
                  <div
                    v-for="group in logScopeGroups(item.formKey)"
                    :key="`${item.priKey}-${group.system}`"
                    class="log-scope__sys"
                    :class="`log-scope__sys--${group.system}`"
                  >
                    <span class="log-scope__sys-name">{{ group.title }}</span>
                    <div class="log-scope__tags">
                      <span
                        v-for="fk in group.items"
                        :key="`${item.priKey}-${fk}`"
                        class="log-scope__tag"
                        :title="formKeyTitle(fk)"
                      >{{ formKeyTitle(fk) }}</span>
                    </div>
                  </div>
                </template>
              </div>
            </template>
            <template #item.assignee="{ item }">
              <span class="text-no-wrap">{{ userLabel(item.assignee) }}</span>
            </template>
            <template #item.proxy="{ item }">
              <span class="text-no-wrap">{{ userLabel(item.proxy) }}</span>
            </template>
            <template #item.period="{ item }">
              <span class="text-no-wrap">
                {{ item.startDate ? convertToTaiwanYear(item.startDate, '-') : '' }}
                <template v-if="item.startDate || item.endDate"> ~ </template>
                {{ item.endDate ? convertToTaiwanYear(item.endDate, '-') : '' }}
              </span>
            </template>
            <template #item.result="{ item }">
              <VChip :color="resultColor(item.result)" variant="flat" size="small" class="rounded-pill">
                {{ resultLabel(item.result) }}
              </VChip>
            </template>
            <template #item.operator="{ item }">
              <span class="text-no-wrap">{{ userLabel(item.operator) }}</span>
            </template>
            <template #item.operateTime="{ item }">
              <span class="text-no-wrap">{{ rocCompact(item.operateTime) }}</span>
            </template>
            <template #item.remark="{ item }">
              <span v-if="!remarkDisplayText(item.remark)" class="text-disabled">-</span>
              <v-tooltip
                v-else
                :model-value="!!remarkTooltipVisible[item.priKey]"
                @update:model-value="(v: boolean) => syncRemarkTooltip(item.priKey, v)"
                location="top"
                interactive
                max-width="480"
                content-class="proxy-remark-tooltip-shell"
                :open-on-hover="!mdAndDown"
                :open-on-click="mdAndDown"
                :close-on-content-click="false"
              >
                <template #activator="{ props }">
                  <span
                    v-bind="props"
                    class="proxy-remark-preview"
                    :class="{ 'text-error': String(item.result) === 'F' }"
                  >
                    {{ remarkPreview(item.remark) }}
                  </span>
                </template>
                <div class="proxy-remark-tooltip-card">
                  <div class="proxy-remark-tooltip-header">
                    <VIcon icon="tabler-message-dots" size="18" color="primary" />
                    <span class="proxy-remark-tooltip-title">備註</span>
                  </div>
                  <div class="proxy-remark-tooltip-body">{{ remarkDisplayText(item.remark) }}</div>
                </div>
              </v-tooltip>
            </template>
            <template #no-data>
              <span v-if="!logLoading">查無資料</span>
            </template>
            <template #bottom>
              <TablePagination
                v-model:page="logPage"
                v-model:size="logSize"
                :items-per-page="logSize"
                :total-items="logTotal"
              />
            </template>
          </VDataTableServer>
      </div>
    </VCard>

    <v-dialog
      v-model="isDialogVisible"
      class="proxy-form-dialog"
      max-width="680px"
      persistent
      scrollable
    >
      <v-card class="proxy-form-card" rounded="lg">
        <div class="proxy-form-card__header">
          <div class="proxy-form-card__header-main">
            <div class="proxy-form-card__header-icon" aria-hidden="true">
              <VIcon
                :icon="dialogStatus === 'add' ? 'tabler-user-plus' : 'tabler-edit'"
                size="22"
              />
            </div>
            <div class="proxy-form-card__header-text">
              <div class="proxy-form-card__title">{{ dialogTitle }}</div>
              <div class="proxy-form-card__subtitle">
                {{ dialogStatus === 'add' ? '設定代理人員、範圍與生效期間' : '調整代理內容後將同步更新同組設定' }}
              </div>
            </div>
          </div>
          <VBtn
            icon
            variant="text"
            size="small"
            aria-label="關閉"
            @click="closeDialog"
          >
            <VIcon icon="tabler-x" size="20" />
          </VBtn>
        </div>

        <v-card-text class="proxy-form-card__body">
          <v-form ref="formRef" class="proxy-form">
            <!-- 代理範圍 -->
            <section class="proxy-form-section">
              <div class="proxy-form-section__head">
                <VIcon icon="tabler-file-text" size="18" />
                <span>代理範圍</span>
                <span class="proxy-form-section__req">必填</span>
              </div>
              <div class="proxy-form-scope-toggle">
                <button
                  type="button"
                  class="proxy-form-scope-option"
                  :class="{ 'proxy-form-scope-option--active': formScopeMode === 'default' }"
                  @click="onFormScopeModeChange('default')"
                >
                  <VIcon icon="tabler-world" size="20" />
                  <div class="proxy-form-scope-option__text">
                    <span class="proxy-form-scope-option__title">不指定範圍</span>
                    <span class="proxy-form-scope-option__desc">套用所有表單</span>
                  </div>
                </button>
                <button
                  type="button"
                  class="proxy-form-scope-option"
                  :class="{ 'proxy-form-scope-option--active': formScopeMode === 'specific' }"
                  @click="onFormScopeModeChange('specific')"
                >
                  <VIcon icon="tabler-list-check" size="20" />
                  <div class="proxy-form-scope-option__text">
                    <span class="proxy-form-scope-option__title">指定範圍</span>
                    <span class="proxy-form-scope-option__desc">選擇一個或多個表單</span>
                  </div>
                </button>
              </div>
              <div
                v-if="formScopeMode === 'specific'"
                class="proxy-form-scope-picker mt-3"
              >
                <div
                  v-if="!formKeySystemGroups.length"
                  class="proxy-form-scope-picker__empty"
                >
                  尚無可選代理範圍
                </div>
                <div
                  v-else
                  class="proxy-form-scope-picker__list"
                >
                  <div
                    v-for="group in formKeySystemGroups"
                    :key="`sys-${group.system}`"
                    class="proxy-form-scope-group"
                    :class="{ 'proxy-form-scope-group--collapsed': !isScopeGroupExpanded(group.system) }"
                  >
                    <div class="proxy-form-scope-group__head">
                      <VCheckbox
                        :model-value="isSystemFullySelected(group)"
                        :indeterminate="isSystemPartiallySelected(group)"
                        density="compact"
                        hide-details
                        color="primary"
                        class="proxy-form-scope-group__check"
                        @click.stop
                        @update:model-value="(v) => toggleSystemScope(group, !!v)"
                      />
                      <button
                        type="button"
                        class="proxy-form-scope-group__toggle"
                        @click="toggleScopeGroupExpand(group.system)"
                      >
                        <span class="proxy-form-scope-group__title">
                          {{ group.title }}
                          <span class="proxy-form-scope-group__total">（{{ group.items.length }}）</span>
                        </span>
                        <span class="proxy-form-scope-group__count">
                          已選 {{ countSelectedInSystem(group) }}/{{ group.items.length }}
                        </span>
                        <VIcon
                          :icon="isScopeGroupExpanded(group.system) ? 'tabler-chevron-up' : 'tabler-chevron-down'"
                          size="18"
                          class="proxy-form-scope-group__chevron"
                        />
                      </button>
                    </div>
                    <div
                      v-show="isScopeGroupExpanded(group.system)"
                      class="proxy-form-scope-group__body"
                    >
                      <label
                        v-for="(item, idx) in group.items"
                        :key="`${group.system}-${item.value}-${idx}`"
                        class="proxy-form-scope-item"
                      >
                        <VCheckbox
                          :model-value="form.formKeys.includes(item.value)"
                          density="compact"
                          hide-details
                          color="primary"
                          @update:model-value="(v) => toggleFormKey(item.value, !!v)"
                        />
                        <span class="proxy-form-scope-item__text" :title="item.title">{{ item.title }}</span>
                      </label>
                    </div>
                  </div>
                </div>
                <div
                  v-if="form.formKeys.length === 0"
                  class="proxy-form-scope-picker__hint text-error"
                >
                  請至少選擇一個代理範圍
                </div>
                <div
                  v-else
                  class="proxy-form-scope-picker__hint"
                >
                  已選 {{ form.formKeys.length }} 項範圍
                </div>
              </div>
            </section>

            <!-- 人員 -->
            <section class="proxy-form-section">
              <div class="proxy-form-section__head">
                <VIcon icon="tabler-users" size="18" />
                <span>代理人員</span>
                <span class="proxy-form-section__req">必填</span>
              </div>
              <div class="proxy-form-people">
                <div class="proxy-form-people__field">
                  <VLabel class="proxy-form-label">
                    被代理人 <span class="text-error">*</span>
                  </VLabel>
                  <AppInput
                    v-model="form.assignee"
                    type="picker"
                    item-title="label"
                    item-value="value"
                    placeholder="請選擇被代理人"
                    :picker="assigneeUserPicker"
                    :queryAll="false"
                    :disabled="!isAdmin || isEnabledReplaceEdit"
                    :clearable="isAdmin && !isEnabledReplaceEdit"
                    :rules="[rules.required]"
                  />
                  <span v-if="!isAdmin" class="proxy-form-hint">被代理人固定為自己</span>
                  <span v-else-if="isEnabledReplaceEdit" class="proxy-form-hint">代理中不可變更被代理人</span>
                </div>
                <div class="proxy-form-people__arrow" aria-hidden="true">
                  <VIcon icon="tabler-arrow-narrow-right" size="22" />
                </div>
                <div class="proxy-form-people__field">
                  <VLabel class="proxy-form-label">
                    代理人 <span class="text-error">*</span>
                  </VLabel>
                  <AppInput
                    v-model="form.proxy"
                    type="picker"
                    item-title="label"
                    item-value="value"
                    placeholder="請選擇代理人"
                    :picker="centerUserPicker"
                    :queryAll="false"
                    clearable
                    :rules="[rules.required, rules.notSelfProxy]"
                  />
                </div>
              </div>
            </section>

            <!-- 期間 -->
            <section class="proxy-form-section">
              <div class="proxy-form-section__head">
                <VIcon icon="tabler-calendar-event" size="18" />
                <span>代理期間</span>
                <span class="proxy-form-section__req">必填</span>
              </div>
              <VRow dense>
                <VCol cols="12" sm="6">
                  <VLabel class="proxy-form-label">
                    開始日期 <span class="text-error">*</span>
                  </VLabel>
                  <AppDateTimePicker
                    v-model="form.startDate"
                    placeholder="請選擇開始日期"
                    :config="{ minDate: 'today' }"
                    :rules="[rules.required, rules.startNotPast]"
                  />
                </VCol>
                <VCol cols="12" sm="6">
                  <VLabel class="proxy-form-label">
                    結束日期 <span class="text-error">*</span>
                  </VLabel>
                  <AppDateTimePicker
                    v-model="form.endDate"
                    placeholder="請選擇結束日期"
                    :config="{ minDate: form.startDate || 'today' }"
                    :rules="[rules.required, rules.endAfterStart]"
                  />
                </VCol>
              </VRow>
              <div v-if="statusHintText" class="proxy-form-status-hint">
                <VIcon icon="tabler-info-circle" size="16" />
                <span>{{ statusHintText }}</span>
              </div>
            </section>

            <!-- 選項／備註 -->
            <section class="proxy-form-section proxy-form-section--last">
              <div class="proxy-form-section__head">
                <VIcon icon="tabler-settings" size="18" />
                <span>其他設定</span>
              </div>
              <div class="proxy-form-option-box">
                <VCheckbox
                  v-model="transferTasks"
                  density="compact"
                  hide-details
                  color="primary"
                  label="同時將被代理人現有的待辦一併轉交給代理人"
                />
              </div>
              <VLabel class="proxy-form-label mt-4">
                備註
                <span class="proxy-form-label__optional">（選填，最多 200 字）</span>
              </VLabel>
              <VTextarea
                v-model="form.remark"
                variant="outlined"
                density="comfortable"
                rows="3"
                auto-grow
                counter
                maxlength="200"
                placeholder="請輸入備註（將記錄於操作紀錄）"
              />
            </section>
          </v-form>
        </v-card-text>

        <div class="proxy-form-card__footer">
          <VBtn
            color="secondary"
            variant="tonal"
            min-width="96"
            @click="closeDialog"
          >
            取消
          </VBtn>
          <VBtn
            color="primary"
            variant="flat"
            min-width="96"
            :loading="submitting"
            @click="save"
          >
            確認
          </VBtn>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script lang="ts">
// 模組層級常數
const DEFAULT_FORM_KEY = "_DEFAULT";
// 表單代碼選項取自簡易代碼表 TBDE23（tabId = FormKey，field2 = value，field3 = title）
const FORM_KEY_TAB_ID = "FormKey";
</script>

<script setup lang="ts">
import { computed, onMounted, reactive, ref, watch } from "vue";
import { useDisplay } from "vuetify";
import TemplateSearchComp from "@/components/template/TemplateSearch.vue";
import { assigneeProxy } from "../api/assigneeProxyApiUrl";
import { getUserInfosByUserNos } from "../api/user";
import { convertToTaiwanYear, toWesternStorageDate } from "@/utils/dateChange";
import { resolveSystemId } from "@/utils/systemId";

// 共用元件為純 JS 未輸出 slot 型別，以寬鬆型別使用以通過模板型別檢查
const TemplateSearch = TemplateSearchComp as any;

const { smAndDown, mdAndDown } = useDisplay();

/** 依目前頁面路徑推斷郵件系統代碼 (TCA / ATH / STK) */
const resolveMailSystemId = () =>
  resolveSystemId(window.location.pathname || document.referrer);

definePage({
  meta: {
    action: "manage",
    subject: "ASSIGNEEPROXYSETTING",
  },
});

/** 代理人設定的單位人員只列出中心人員（部門 orgType = C） */
const centerUserPicker = {
  ...duUserPicker,
  leftParams: {
    ...duUserPicker.leftParams,
    orgType: "C",
  },
  rightParams: {
    ...duUserPicker.rightParams,
    orgType: "C",
  },
};

/** 被代理人 picker：dialog 內可勾選「包含已離營」；仍限中心人員 */
const assigneeUserPicker = {
  ...centerUserPicker,
  includeLeftCamp: true,
};

const formRef = ref();
const rules = {
  required: (value: any) => (Array.isArray(value) ? value.length > 0 : !!value) || "此欄位為必填",
  startNotPast: (value: any) => {
    if (!value) return true;
    const start = toWesternStorageDate(String(value));
    if (!start || start.length !== 8) return true;
    const now = new Date();
    const today = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, "0")}${String(now.getDate()).padStart(2, "0")}`;
    return start >= today || "開始日期不可早於今天";
  },
  endAfterStart: (value: any) =>
    !value || !form.startDate || ymd8(value) >= ymd8(form.startDate) || "結束日期不可早於開始日期",
  notSelfProxy: (value: any) => {
    const assigneeCode = String(
      isAdmin.value ? toCode(form.assignee) : currentUserNo.value,
    ).trim().toUpperCase();
    const proxyCode = String(toCode(value)).trim().toUpperCase();
    return !proxyCode || !assigneeCode || proxyCode !== assigneeCode || "代理人不可與被代理人相同";
  },
};

const page = ref(1);
const size = ref(100);
const totalData = ref(0);
const loading = ref(true);
const isLoadingMore = ref(false);
/** 手機無限捲動：下一頁的 0-based pageNo */
const mobileNextPageNo = ref(0);
const MOBILE_PAGE_SIZE = 5;
const DESKTOP_PAGE_SIZE = 100;
const disablingId = ref<string | null>(null);
const items = reactive<any[]>([]);
const hasMoreProxyList = computed(() => displayItems.value.length < totalData.value);

/** 同組 key：_DEFAULT 單獨一組；指定範圍依人／期間／狀態合併 */
const siblingGroupKey = (r: any) => {
  const fk = (r.formKeys && r.formKeys[0]) || r.formKey || DEFAULT_FORM_KEY;
  if (fk === DEFAULT_FORM_KEY) return `default:${r.id}`;
  return [
    String(toCode(r.assignee) || "").trim().toUpperCase(),
    String(toCode(r.proxy) || "").trim().toUpperCase(),
    toWesternStorageDate(String(r.startDate ?? "")),
    toWesternStorageDate(String(r.endDate ?? "")),
    String(r.status ?? ""),
  ].join("|");
};

const groupProxyRows = (rows: any[]) => {
  const map = new Map<string, any>();
  for (const r of rows) {
    const k = siblingGroupKey(r);
    const rowIds = Array.isArray(r.ids) && r.ids.length
      ? r.ids.map(String)
      : [String(r.id)];
    const rowKeys = Array.isArray(r.formKeys) && r.formKeys.length
      ? r.formKeys.map(String)
      : [r.formKey || DEFAULT_FORM_KEY];
    if (!map.has(k)) {
      map.set(k, {
        ...r,
        ids: [...rowIds],
        formKeys: [...rowKeys],
      });
    } else {
      const g = map.get(k);
      for (const id of rowIds) {
        if (!g.ids.includes(id)) g.ids.push(id);
      }
      for (const fk of rowKeys) {
        if (!g.formKeys.includes(fk)) g.formKeys.push(fk);
      }
    }
  }
  return [...map.values()].map((g) => {
    const keys = [...new Set(g.formKeys as string[])];
    return { ...g, formKeys: keys, formKey: keys[0] };
  });
};

/** 後端已依同組分頁；前端再合併一次以相容舊資料／跨頁同組 */
const displayItems = computed(() => groupProxyRows(items));

const itemFormKeys = (item: any): string[] =>
  item?.formKeys?.length
    ? item.formKeys
    : [item?.formKey || DEFAULT_FORM_KEY];

const isDefaultScope = (item: any) =>
  itemFormKeys(item).length === 1 && itemFormKeys(item)[0] === DEFAULT_FORM_KEY;

/** 清單卡片：代理範圍依系統分組 */
const itemScopeSystemGroups = (item: any) => {
  const buckets = new Map<string, string[]>();
  for (const fk of itemFormKeys(item)) {
    if (!fk || fk === DEFAULT_FORM_KEY) continue;
    const sys = normalizeFormKeySystem(formKeySystemMap[fk] ?? "0");
    if (!buckets.has(sys)) buckets.set(sys, []);
    buckets.get(sys)!.push(fk);
  }
  return [...buckets.entries()]
    .map(([system, items]) => ({
      system,
      title: formKeySystemTitle(system),
      order: FORM_KEY_SYSTEM_META[system]?.order ?? 99,
      items,
    }))
    .sort((a, b) => a.order - b.order || a.title.localeCompare(b.title, "zh-Hant"));
};

const findSiblingRowsInList = (list: any[], row: any) => {
  if (!row) return [];
  const fk = row.formKey || DEFAULT_FORM_KEY;
  if (fk === DEFAULT_FORM_KEY) {
    return list.filter((r) => String(r.id) === String(row.id));
  }
  const key = siblingGroupKey(row);
  return list.filter((r) => {
    if ((r.formKey || DEFAULT_FORM_KEY) === DEFAULT_FORM_KEY) return false;
    return siblingGroupKey(r) === key;
  });
};

const rowGroupIds = (row: any) => {
  if (Array.isArray(row?.ids) && row.ids.length) return row.ids.map(String);
  return [String(row.id)];
};

const onFormScopeModeChange = (mode: "default" | "specific" | null) => {
  if (mode === "default" || mode == null) {
    formScopeMode.value = "default";
    form.formKeys = [];
    return;
  }
  formScopeMode.value = "specific";
  if (!form.formKeys.length) {
    form.formKeys = [];
  }
};

const isAdmin = ref(false);
const currentUserNo = ref("");
const currentUserPickerValue = ref("");

// ===== 檢視切換（代理人設定 / 操作紀錄）=====
const activeTab = ref<"setting" | "log">("setting");
const viewOptions = [
  { value: "setting", label: "代理人設定", icon: "tabler-user-cog" },
  { value: "log", label: "操作紀錄", icon: "tabler-history" },
] as const;
const viewActiveIndex = computed(() =>
  viewOptions.findIndex(o => o.value === activeTab.value)
);
const logItems = reactive<any[]>([]);
const logPage = ref(1);
const logSize = ref(20);
const logTotal = ref(0);
const logLoading = ref(false);
const logFilters = reactive<{ action: string | null }>({ action: null });
const logActionOptions = [
  { value: "CREATE", title: "新增" },
  { value: "UPDATE", title: "修改" },
  { value: "ENABLE", title: "啟用" },
  { value: "DISABLE", title: "停用" },
  { value: "DELETE", title: "刪除" },
];
const logHeaders = [
  { title: "操作", key: "action", sortable: false, width: "90px" },
  { title: "代理範圍", key: "formKey", sortable: false, width: "280px" },
  { title: "被代理人", key: "assignee", sortable: false, width: "160px" },
  { title: "代理人", key: "proxy", sortable: false, width: "160px" },
  { title: "期間", key: "period", sortable: false, width: "190px" },
  { title: "結果", key: "result", sortable: false, width: "90px" },
  { title: "操作者", key: "operator", sortable: false, width: "160px" },
  { title: "操作時間", key: "operateTime", sortable: false, width: "170px" },
  { title: "備註", key: "remark", sortable: false, width: "220px" },
];
const ACTION_LABELS: Record<string, string> = {
  CREATE: "新增", UPDATE: "修改", ENABLE: "啟用", DISABLE: "停用", DELETE: "刪除",
};
const ACTION_COLORS: Record<string, string> = {
  CREATE: "primary", UPDATE: "warning", ENABLE: "success", DISABLE: "secondary", DELETE: "error",
};
const actionLabel = (a: any) => ACTION_LABELS[String(a)] ?? String(a ?? "");
const actionColor = (a: any) => ACTION_COLORS[String(a)] ?? "secondary";
const resultLabel = (r: any) => (String(r) === "S" ? "成功" : String(r) === "F" ? "失敗" : String(r ?? ""));
const resultColor = (r: any) => (String(r) === "S" ? "success" : String(r) === "F" ? "error" : "secondary");
// 備註預覽（比照結訓單簽章報告表：列上顯示截斷預覽，滑入／點擊以 tooltip 卡片看全文）
const REMARK_PREVIEW_MAX = 20;
/** 備註預覽：略過後端自動附加的「同組範圍…」（已改在代理範圍欄顯示） */
const remarkDisplayText = (s: any) => {
  const t = String(s ?? "").trim();
  if (!t) return "";
  return t
    .replace(/；?\s*同組範圍共\s*\d+\s*項：[^\n]*/g, "")
    .replace(/^同組範圍共\s*\d+\s*項：[^\n]*/g, "")
    .trim();
};
const remarkPreview = (s: any) => {
  const t = remarkDisplayText(s);
  if (!t) return "";
  return t.length <= REMARK_PREVIEW_MAX ? t : `${t.slice(0, REMARK_PREVIEW_MAX)}...`;
};
const remarkTooltipVisible = ref<Record<string, boolean>>({});
// 窄螢：同一時間僅允許一則備註 tooltip
const syncRemarkTooltip = (key: string, open: boolean) => {
  if (open && mdAndDown.value) {
    remarkTooltipVisible.value = { [key]: true };
    return;
  }
  remarkTooltipVisible.value = { ...remarkTooltipVisible.value, [key]: open };
};

// yyyyMMddHHmmss -> 民國 yyy-MM-dd HH:mm:ss
const rocCompact = (s: any) => {
  if (!s) return "";
  const v = String(s).trim();
  if (v.length < 8) return v;
  const y = parseInt(v.slice(0, 4), 10) - 1911;
  const hh = v.length >= 10 ? v.slice(8, 10) : "00";
  const mi = v.length >= 12 ? v.slice(10, 12) : "00";
  const ss = v.length >= 14 ? v.slice(12, 14) : "00";
  return `${y}-${v.slice(4, 6)}-${v.slice(6, 8)} ${hh}:${mi}:${ss}`;
};

// 表單代碼選項：value=field2、title=field3、system=field4（TBDE23 tabId=FormKey）
type FormKeyOption = { value: string; title: string; system: string };
const FORM_KEY_SYSTEM_META: Record<string, { title: string; order: number }> = {
  "1": { title: "協會系統", order: 1 },
  "2": { title: "選手系統", order: 2 },
  "3": { title: "消耗品系統", order: 3 },
  "0": { title: "其他", order: 9 },
};
const normalizeFormKeySystem = (raw: unknown): string => {
  if (raw == null || raw === "") return "0";
  const n = Number(raw);
  if (!Number.isNaN(n)) {
    const s = String(Math.trunc(n));
    if (s in FORM_KEY_SYSTEM_META) return s;
  }
  const s = String(raw).trim();
  return s in FORM_KEY_SYSTEM_META ? s : "0";
};
const formKeySystemTitle = (system: string) =>
  FORM_KEY_SYSTEM_META[system]?.title ?? "其他";

const formKeyOptions = ref<FormKeyOption[]>([
  { value: DEFAULT_FORM_KEY, title: "不指定範圍", system: "0" },
]);
// 表單代碼 -> 顯示名稱 對照（供清單顯示）
const formKeyMap = reactive<Record<string, string>>({ [DEFAULT_FORM_KEY]: "不指定範圍" });
const formKeySystemMap = reactive<Record<string, string>>({ [DEFAULT_FORM_KEY]: "0" });
const formKeyTitle = (code: string) => {
  if (!code) return "";
  return formKeyMap[code] || code;
};
const formKeyLabel = (code: string) => {
  if (!code) return "";
  if (code.includes(",")) {
    return code
      .split(",")
      .map((c) => c.trim())
      .filter(Boolean)
      .map((c) => formKeyLabelSingle(c))
      .join("、");
  }
  return formKeyLabelSingle(code);
};
const formKeyLabelSingle = (code: string) => {
  if (!code) return "";
  const etcMatch = code.match(/^(.+)等(\d+)項$/);
  if (etcMatch) {
    return `${formKeyLabelSingle(etcMatch[1])}等${etcMatch[2]}項`;
  }
  if (code === DEFAULT_FORM_KEY) return formKeyMap[code] || code;
  const title = formKeyTitle(code);
  const sys = formKeySystemMap[code];
  return sys && sys !== "0" ? `【${formKeySystemTitle(sys)}】${title}` : title;
};

/** 操作紀錄 formKey：解析為代碼陣列（支援逗號多選） */
const logScopeKeys = (raw: any): string[] => {
  const s = String(raw ?? "").trim();
  if (!s) return [];
  if (s === DEFAULT_FORM_KEY) return [DEFAULT_FORM_KEY];
  if (/^.+等\d+項$/.test(s)) return [];
  if (s.includes(",")) {
    return [...new Set(s.split(",").map((c) => c.trim()).filter(Boolean))];
  }
  return [s];
};

const isLogDefaultScope = (raw: any) => {
  const keys = logScopeKeys(raw);
  return keys.length === 1 && keys[0] === DEFAULT_FORM_KEY;
};

/** 後端縮寫「首筆等N項」時，僅顯示摘要 */
const logScopeMeta = (raw: any) => {
  const s = String(raw ?? "").trim();
  const m = s.match(/^(.+)等(\d+)項$/);
  if (!m) return { truncated: false, count: 0, hint: "" };
  const first = m[1];
  const count = Number(m[2]) || 0;
  const firstTitle = formKeyTitle(first) || first;
  return {
    truncated: true,
    count,
    hint: firstTitle ? `含 ${firstTitle}…` : "",
  };
};

const logScopeGroups = (raw: any) => {
  const buckets = new Map<string, string[]>();
  for (const fk of logScopeKeys(raw)) {
    if (!fk || fk === DEFAULT_FORM_KEY) continue;
    const sys = normalizeFormKeySystem(formKeySystemMap[fk] ?? "0");
    if (!buckets.has(sys)) buckets.set(sys, []);
    buckets.get(sys)!.push(fk);
  }
  return [...buckets.entries()]
    .map(([system, items]) => ({
      system,
      title: formKeySystemTitle(system),
      order: FORM_KEY_SYSTEM_META[system]?.order ?? 99,
      items,
    }))
    .sort((a, b) => a.order - b.order || a.title.localeCompare(b.title, "zh-Hant"));
};
/** 指定範圍選項（不含不指定） */
const specificFormKeyOptions = computed(() =>
  formKeyOptions.value.filter((o) => o.value !== DEFAULT_FORM_KEY),
);
/** 指定範圍：依 FIELD4 系統分組 */
const formKeySystemGroups = computed(() => {
  const buckets = new Map<string, FormKeyOption[]>();
  for (const o of specificFormKeyOptions.value) {
    const sys = normalizeFormKeySystem(o.system);
    if (!buckets.has(sys)) buckets.set(sys, []);
    buckets.get(sys)!.push(o);
  }
  return [...buckets.entries()]
    .map(([system, items]) => ({
      system,
      title: formKeySystemTitle(system),
      order: FORM_KEY_SYSTEM_META[system]?.order ?? 99,
      items,
    }))
    .sort((a, b) => a.order - b.order || a.title.localeCompare(b.title, "zh-Hant"));
});
/** 搜尋列：僅「不指定範圍」＋三個系統（不含表單細項） */
const SEARCH_SYSTEM_PREFIX = "sys:";
const SEARCH_SYSTEM_CODES = ["1", "2", "3"] as const;
const searchSystemToken = (system: string) => `${SEARCH_SYSTEM_PREFIX}${system}`;
const parseSearchSystemToken = (value: string) =>
  value.startsWith(SEARCH_SYSTEM_PREFIX) ? value.slice(SEARCH_SYSTEM_PREFIX.length) : "";

const searchFormKeyOptions = computed(() => {
  const items: Array<{ title: string; value: string; system?: string }> = [
    {
      title: formKeyMap[DEFAULT_FORM_KEY] || "不指定範圍",
      value: DEFAULT_FORM_KEY,
      system: "0",
    },
  ];
  for (const system of SEARCH_SYSTEM_CODES) {
    items.push({
      title: formKeySystemTitle(system),
      value: searchSystemToken(system),
      system,
    });
  }
  return items;
});
const formKeySystemChipColor = (system: unknown) => {
  const sys = normalizeFormKeySystem(system);
  if (sys === "1") return "info";
  if (sys === "2") return "warning";
  if (sys === "3") return "success";
  return "secondary";
};

const searchSelectedFormKeys = computed(() =>
  Array.isArray(filters.formKey) ? filters.formKey.map(String).filter(Boolean) : [],
);

/** 搜尋代理範圍：單選（不指定範圍 或 單一系統） */
const searchFormKeySingle = computed({
  get: () => searchSelectedFormKeys.value[0] ?? null,
  set: (val: string | null) => {
    filters.formKey = val ? [String(val)] : [];
  },
});

const findSearchSystemGroup = (system: string) =>
  formKeySystemGroups.value.find((g) => g.system === system);

/** 將搜尋選項展開為實際 formKey（系統 → 該系統全部表單） */
const resolveSearchFormKeyQuery = () => {
  const selected = searchSelectedFormKeys.value[0];
  if (!selected) return "";
  if (selected === DEFAULT_FORM_KEY) return DEFAULT_FORM_KEY;
  const sys = parseSearchSystemToken(selected);
  if (sys) {
    const group = findSearchSystemGroup(sys);
    if (!group?.items.length) return "";
    return group.items.map((it) => it.value).join(",");
  }
  return selected;
};

const countSelectedInSystem = (group: { items: FormKeyOption[] }) =>
  group.items.filter((it) => form.formKeys.includes(it.value)).length;

const isSystemFullySelected = (group: { items: FormKeyOption[] }) =>
  group.items.length > 0 && countSelectedInSystem(group) === group.items.length;

const isSystemPartiallySelected = (group: { items: FormKeyOption[] }) => {
  const n = countSelectedInSystem(group);
  return n > 0 && n < group.items.length;
};

const toggleFormKey = (value: string, checked: boolean) => {
  const set = new Set(form.formKeys);
  if (checked) set.add(value);
  else set.delete(value);
  form.formKeys = [...set];
};

const toggleSystemScope = (group: { items: FormKeyOption[]; system: string }, checked: boolean) => {
  const set = new Set(form.formKeys);
  for (const it of group.items) {
    if (checked) set.add(it.value);
    else set.delete(it.value);
  }
  form.formKeys = [...set];
  if (checked) {
    expandedScopeSystems.value = new Set([...expandedScopeSystems.value, group.system]);
  }
};

/** 指定範圍分組展開狀態（預設全部展開） */
const expandedScopeSystems = ref<Set<string>>(new Set(["0", "1", "2", "3"]));
const isScopeGroupExpanded = (system: string) => expandedScopeSystems.value.has(system);
const toggleScopeGroupExpand = (system: string) => {
  const next = new Set(expandedScopeSystems.value);
  if (next.has(system)) next.delete(system);
  else next.add(system);
  expandedScopeSystems.value = next;
};

// 狀態：0=未啟用、1=代理中、2=已結束、P=待生效（開始日未到，本地尚未同步 EZoForm）
const STATUS_LABELS: Record<string, string> = {
  "0": "未啟用",
  "1": "代理中",
  "2": "已結束",
  P: "待生效",
};
const STATUS_COLORS: Record<string, string> = {
  "0": "warning",
  "1": "success",
  "2": "secondary",
  P: "info",
};
const statusLabel = (status: any) => {
  if (status == null || status === "") return "";
  return STATUS_LABELS[String(status)] ?? String(status);
};
const STATUS_ICONS: Record<string, string> = {
  "0": "tabler-clock-pause",
  "1": "tabler-circle-check",
  "2": "tabler-ban",
  P: "tabler-calendar-event",
};
const statusColor = (status: any) => STATUS_COLORS[String(status)] ?? "secondary";
const statusIcon = (status: any) => STATUS_ICONS[String(status)] ?? "tabler-circle-dot";
// 狀態下拉選項（未啟用不提供搜尋，新增後即啟用／待生效）
const statusOptions = [
  { value: "P", title: "待生效" },
  { value: "1", title: "代理中" },
  { value: "2", title: "已結束" },
];

const fetchFormKeyOptions = async () => {
  try {
    const resp: any = await $api("/q/de/select/select/select/deSelectField23", {
      method: "GET",
      query: { tabId: FORM_KEY_TAB_ID },
    });
    const list: any[] = Array.isArray(resp) ? resp : (resp?.data ?? []);
    const seen = new Set<string>();
    const opts: FormKeyOption[] = [];
    for (const it of list) {
      if (!it?.value) continue;
      const value = String(it.value).trim();
      if (!value || seen.has(value)) continue;
      seen.add(value);
      const title = String(it.label ?? value).trim() || value;
      const system = normalizeFormKeySystem(it.field4 ?? it.desc);
      opts.push({ value, title, system });
      formKeyMap[value] = title;
      formKeySystemMap[value] = system;
    }
    formKeyOptions.value = [
      { value: DEFAULT_FORM_KEY, title: "不指定範圍", system: "0" },
      ...opts,
    ];
    // 預設展開所有系統分組
    expandedScopeSystems.value = new Set(formKeySystemGroups.value.map((g) => g.system));
  } catch (error) {
    console.error("取得表單代碼選項失敗:", error);
  }
};

// 員編 -> 姓名 對照（供清單顯示，作為值未內含姓名時的後備）
const nameMap = reactive<Record<string, string>>({});

// EZoForm 回傳值可能為「員編 + CODE_DELIMETER + 姓名」(例: 16882^@`卓昱廷)
// 解析後以「員編 姓名」格式顯示；若值未內含姓名，再以 nameMap 後備。
const userLabel = (raw: string) => {
  if (!raw) return "";
  const code = toCode(raw);
  const text = toText(raw);
  const name = text && text !== code ? text : nameMap[code] || "";
  return name ? `${name}(${code})` : code;
};

/** AppInput picker 顯示用：轉成「員編 + CODE_DELIMETER + 姓名」 */
const toPickerValue = (raw: string) => {
  if (!raw) return "";
  const code = String(toCode(raw) || "").trim();
  if (!code) return "";
  const embedded = toText(raw);
  const name =
    embedded && embedded !== code
      ? embedded
      : nameMap[code] || nameMap[code.toUpperCase()] || nameMap[code.toLowerCase()] || "";
  return name ? toCodeText(code, name) : code;
};

// 操作按鈕僅該列的被代理人可見；Administrator 不限。代理人僅可檢視。
const canOperate = (item: any) => {
  if (isAdmin.value) return true;
  const me = String(currentUserNo.value || "").toUpperCase();
  if (!me) return false;
  return String(toCode(item?.assignee) || "").toUpperCase() === me;
};

const loadUserNames = async (rows: any[]) => {
  const nos = [...new Set(rows.flatMap((r) => [toCode(r.assignee), toCode(r.proxy), toCode(r.createUserId), toCode(r.updateUserId), toCode(r.operator)]).filter(Boolean))];
  const missing = nos.filter((no) => !nameMap[no]);
  if (missing.length === 0) return;
  const users = await getUserInfosByUserNos(missing);
  for (const u of users) {
    const no = u?.userNo != null ? String(u.userNo).trim() : "";
    if (no) nameMap[no] = (u?.userName ?? u?.cname ?? no) as string;
  }
};

const filters = reactive({
  formKey: [] as string[],
  assignee: "",
  proxy: "",
  date: "",
  // 預設不篩狀態（空陣列＝全部）
  status: [] as string[],
});

const isDialogVisible = ref(false);
const dialogTitle = ref("");
const dialogStatus = ref<"add" | "edit">("add");
const submitting = ref(false);

const formOriginal = {
  id: "",
  formKeys: [] as string[],
  assignee: "",
  proxy: "",
  startDate: "",
  endDate: "",
  status: "1",
  remark: "",
};
const form = reactive({ ...formOriginal, formKeys: [] as string[] });
/** default=不指定範圍；specific=多選指定 */
const formScopeMode = ref<"default" | "specific">("default");
/** 編輯中的同組所有 id（啟用中修改／停用／刪除用） */
const editingGroupIds = ref<string[]>([]);
// 編輯前的原始狀態，用於判斷是否需呼叫 enable/disable
const originalStatus = ref("0");
/** 編輯開啟時的被代理人員編（啟用中修改不可變更） */
const originalAssignee = ref("");
// 啟用時是否一併將被代理人現有待辦轉交給代理人
const transferTasks = ref(false);

/** 是否為「啟用中」的修改（不可原地 update，需停用後新建） */
const isEnabledReplaceEdit = computed(
  () => dialogStatus.value === "edit" && originalStatus.value === "1",
);

/** 開始日是否晚於今天（西元 yyyyMMdd） */
const isStartDateInFuture = computed(() => {
  const start = toWesternStorageDate(form.startDate);
  if (!start || start.length !== 8) return false;
  const now = new Date();
  const today = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, "0")}${String(now.getDate()).padStart(2, "0")}`;
  return start > today;
});

const statusHintText = computed(() => {
  if (isEnabledReplaceEdit.value) {
    return "儲存後會先停用原設定（待辦先還回），再建立並啟用新設定。若要結束代理請按清單「停用」。";
  }
  if (isStartDateInFuture.value) {
    return "開始日尚未到：先存為「待生效」，開始日當天 00:00 才同步至表單系統並啟用／轉交待辦。";
  }
  return "";
});

// 日期工具：yyyy-MM-dd <-> yyyyMMdd
const ymd8 = (d: string) => (d ? String(d).replace(/-/g, "") : "");
// 啟用/作廢時間：EZoForm 回傳「2026/06/03 12:16:23」，
// 將開頭西元年轉民國年，並把日期分隔符 / 改為 -（時間的 : 保留）
const rocDateTime = (s: string) => {
  if (!s) return "";
  const str = String(s).trim();
  const m = str.match(/^(\d{4})([\s\S]*)$/);
  if (!m) return str;
  return `${parseInt(m[1], 10) - 1911}${m[2].replace(/\//g, "-")}`;
};
const ymdDash = (s: string) => {
  if (!s) return "";
  const v = String(s).trim();
  // 民國 YYYMMDD
  if (/^\d{7}$/.test(v)) {
    return `${v.slice(0, 3)}-${v.slice(3, 5)}-${v.slice(5, 7)}`;
  }
  // 西元 YYYYMMDD → 民國 YYY-MM-DD（供日期選擇器）
  if (/^\d{8}$/.test(v)) {
    const year = parseInt(v.slice(0, 4), 10);
    if (year >= 1911) {
      const roc = year - 1911;
      return `${roc}-${v.slice(4, 6)}-${v.slice(6, 8)}`;
    }
    return `${v.slice(0, 4)}-${v.slice(4, 6)}-${v.slice(6, 8)}`;
  }
  return v;
};

const popSwal = (icon: string, title: string, text = "") => {
  $swal({
    position: "top-end",
    icon,
    title,
    text,
    showConfirmButton: false,
    timer: icon === "success" ? 1500 : undefined,
    customClass: { confirmButton: "btn btn-primary" },
    buttonsStyling: false,
    // 確保提示顯示在 v-dialog 之上（Vuetify overlay z-index 較高）
    didOpen: () => {
      const container = document.querySelector(".swal2-container") as HTMLElement | null;
      if (container) container.style.zIndex = "24000";
    },
  });
};

// 設定資訊（是否管理員、目前使用者）
const fetchConfig = async () => {
  try {
    const resp: any = await $api(assigneeProxy.config, { method: "GET" });
    isAdmin.value = !!resp?.isAdmin;
    currentUserNo.value = resp?.userNo ?? "";
    currentUserPickerValue.value = resp?.userPickerValue || currentUserNo.value;
  } catch (error) {
    isAdmin.value = false;
    currentUserNo.value = "";
    currentUserPickerValue.value = "";
    console.error("取得代理人設定資訊失敗:", error);
  }
};

// 清單查詢（手機 append 累加；桌面／重查則覆蓋）
const fetchData = async (opts?: { append?: boolean }) => {
  const append = !!opts?.append && smAndDown.value;
  if (append) {
    if (loading.value || isLoadingMore.value || !hasMoreProxyList.value) return;
    isLoadingMore.value = true;
  } else {
    loading.value = true;
    mobileNextPageNo.value = 0;
    if (smAndDown.value) {
      page.value = 1;
    }
  }
  try {
    const pageSize = smAndDown.value ? MOBILE_PAGE_SIZE : size.value;
    const pageNo = smAndDown.value
      ? (append ? mobileNextPageNo.value : 0)
      : page.value - 1;
    const resp: any = await $api(assigneeProxy.getPage, {
      method: "GET",
      query: {
        formKey: resolveSearchFormKeyQuery(),
        assignee: filters.assignee ? toCode(filters.assignee) : "",
        proxy: filters.proxy ? toCode(filters.proxy) : "",
        date: filters.date ? ymd8(filters.date) : "",
        status: filters.status && filters.status.length ? filters.status.join(",") : "",
        pageNo,
        pageSize,
      },
    });
    totalData.value = resp?.totalElements ?? 0;
    const content = resp?.content ?? [];
    if (append) {
      items.push(...content);
      mobileNextPageNo.value = pageNo + 1;
    } else {
      items.splice(0, items.length, ...content);
      mobileNextPageNo.value = content.length ? 1 : 0;
    }
    loadUserNames(content);
  } catch (error) {
    if (!append) {
      totalData.value = 0;
      items.splice(0, items.length);
      mobileNextPageNo.value = 0;
    }
    console.error("查詢代理人設定清單失敗:", error);
  } finally {
    loading.value = false;
    isLoadingMore.value = false;
  }
};

const onMobileIntersect = (isIntersecting: boolean) => {
  if (isIntersecting && smAndDown.value) {
    fetchData({ append: true });
  }
};

// 操作紀錄查詢（僅 Administrator）
const fetchLogData = async () => {
  if (!isAdmin.value) return;
  logLoading.value = true;
  try {
    const resp: any = await $api(assigneeProxy.logPage, {
      method: "GET",
      query: {
        action: logFilters.action || "",
        pageNo: logPage.value - 1,
        pageSize: logSize.value,
      },
    });
    logTotal.value = resp?.totalElements ?? 0;
    const content = resp?.content ?? [];
    logItems.splice(0, logItems.length, ...content);
    loadUserNames(content);
  } catch (error) {
    logTotal.value = 0;
    logItems.splice(0, logItems.length);
    console.error("查詢代理人設定操作紀錄失敗:", error);
  } finally {
    logLoading.value = false;
  }
};

// 首次切到操作紀錄 tab 時載入
watch(activeTab, (tab) => {
  // 切換檢視時皆重新整理對應資料
  if (tab === "log") {
    if (isAdmin.value) fetchLogData();
  } else {
    fetchData();
  }
});
watch([logPage, logSize, () => logFilters.action], () => {
  if (activeTab.value === "log") fetchLogData();
});

const openDialog = async (type: "add" | "edit", row?: any) => {
  dialogStatus.value = type;
  dialogTitle.value = type === "add" ? "新增代理人設定" : "修改代理人設定";

  transferTasks.value = false;
  editingGroupIds.value = [];
  if (type === "add") {
    Object.assign(form, { ...formOriginal, formKeys: [] as string[] });
    form.status = "1";
    formScopeMode.value = "default";
    originalStatus.value = "0";
    originalAssignee.value = "";
    if (!isAdmin.value) form.assignee = currentUserPickerValue.value;
  } else {
    if (!row) return;
    await loadUserNames([row]);
    const all = await fetchAllProxySettings();
    const siblings = findSiblingRowsInList(all, {
      id: row.ids?.[0] ?? row.id,
      formKey: (row.formKeys && row.formKeys[0]) || row.formKey,
      assignee: row.assignee,
      proxy: row.proxy,
      startDate: row.startDate,
      endDate: row.endDate,
      status: row.status,
    });
    const siblingList = siblings.length ? siblings : [row];
    editingGroupIds.value = siblingList.map((r) => String(r.id));
    const keys = [...new Set(siblingList.map((r) => r.formKey || DEFAULT_FORM_KEY))];
    form.id = String(siblingList[0].id);
    form.formKeys = keys.filter((k) => k !== DEFAULT_FORM_KEY);
    formScopeMode.value = keys.includes(DEFAULT_FORM_KEY) ? "default" : "specific";
    if (formScopeMode.value === "specific" && form.formKeys.length === 0) {
      form.formKeys = keys;
    }
    form.assignee = toPickerValue(row.assignee ?? "");
    form.proxy = toPickerValue(row.proxy ?? "");
    form.startDate = ymdDash(row.startDate);
    form.endDate = ymdDash(row.endDate);
    form.status = "1";
    form.remark = "";
    originalStatus.value = String(row.status ?? "0");
    originalAssignee.value = String(toCode(row.assignee ?? "") || "").trim();
    transferTasks.value = String(row.transferTasks ?? "") === "Y";
    if (!isAdmin.value) form.assignee = currentUserPickerValue.value;
  }
  isDialogVisible.value = true;
};

const closeDialog = () => {
  isDialogVisible.value = false;
};

const resolvedFormKeys = () => {
  if (formScopeMode.value === "default") return [DEFAULT_FORM_KEY];
  return [...new Set(form.formKeys.filter(Boolean))];
};

const buildPayload = () => ({
  formKey: resolvedFormKeys()[0] || DEFAULT_FORM_KEY,
  formKeys: resolvedFormKeys(),
  assignee: isAdmin.value ? toCode(form.assignee) : currentUserNo.value,
  proxy: toCode(form.proxy),
  startDate: toWesternStorageDate(form.startDate),
  endDate: toWesternStorageDate(form.endDate),
  remark: form.remark || "",
  status: form.status,
  transferTasks: transferTasks.value,
});

// 同一時段且表單範圍衝突時：
// - 同一被代理人不可再設（重複設定）
// - 被代理人／代理人角色交錯視為代理鏈（如 B→A 再 A→C）
// - 同一代理人可同時代理多人（B→A 與 C→A 不算代理鏈）
const PROXY_CONFLICT_MSG = (
  name: string,
  reason: string,
  overlapStart?: string,
  overlapEnd?: string,
) => {
  const period =
    overlapStart && overlapEnd
      ? `（${convertToTaiwanYear(overlapStart, "-")}～${convertToTaiwanYear(overlapEnd, "-")}）`
      : "";
  return `${name} 在相同表單範圍與重疊期間${period}內${reason}`;
};

const formKeysConflict = (a: string | null | undefined, b: string | null | undefined) => {
  const na = a || DEFAULT_FORM_KEY;
  const nb = b || DEFAULT_FORM_KEY;
  return na === DEFAULT_FORM_KEY || nb === DEFAULT_FORM_KEY || na === nb;
};

const datesOverlap = (start1: string, end1: string, start2: string, end2: string) => {
  const s1 = toWesternStorageDate(String(start1 ?? "").trim());
  const e1 = toWesternStorageDate(String(end1 ?? "").trim());
  const s2 = toWesternStorageDate(String(start2 ?? "").trim());
  const e2 = toWesternStorageDate(String(end2 ?? "").trim());
  if (!s1 || !e1 || !s2 || !e2) return false;
  return s1 <= e2 && s2 <= e1;
};

/** 兩段期間的實際重疊起迄（yyyyMMdd）；無重疊則回 null */
const overlapRange = (start1: string, end1: string, start2: string, end2: string) => {
  const s1 = toWesternStorageDate(String(start1 ?? "").trim());
  const e1 = toWesternStorageDate(String(end1 ?? "").trim());
  const s2 = toWesternStorageDate(String(start2 ?? "").trim());
  const e2 = toWesternStorageDate(String(end2 ?? "").trim());
  if (!s1 || !e1 || !s2 || !e2 || !(s1 <= e2 && s2 <= e1)) return null;
  return {
    start: s1 > s2 ? s1 : s2,
    end: e1 < e2 ? e1 : e2,
  };
};

const fetchAllProxySettings = async () => {
  const resp: any = await $api(assigneeProxy.getPage, {
    method: "GET",
    query: { pageNo: 0, pageSize: 1000 },
  });
  // 後端回同組卡片；衝突／找同組需展開成「每 formKey 一列」
  return expandProxyGroups(resp?.content ?? []);
};

/** 將同組卡片展開為單 formKey 列（相容衝突檢查與同組查找） */
const expandProxyGroups = (list: any[]) => {
  const out: any[] = [];
  for (const r of list) {
    const ids = Array.isArray(r.ids) && r.ids.length ? r.ids.map(String) : [String(r.id)];
    const keys = Array.isArray(r.formKeys) && r.formKeys.length
      ? r.formKeys.map(String)
      : [r.formKey || DEFAULT_FORM_KEY];
    const n = Math.max(ids.length, keys.length);
    for (let i = 0; i < n; i++) {
      out.push({
        ...r,
        id: ids[Math.min(i, ids.length - 1)],
        formKey: keys[Math.min(i, keys.length - 1)],
        ids: undefined,
        formKeys: undefined,
      });
    }
  }
  return out;
};

const findProxyPersonConflictInList = (
  list: any[],
  params: {
    formKey: string;
    assignee: string;
    proxy: string;
    startDate: string;
    endDate: string;
    excludeId?: string;
    excludeIds?: string[];
  },
) => {
  const newFormKey = params.formKey || DEFAULT_FORM_KEY;
  const assigneeCode = String(toCode(params.assignee)).trim().toUpperCase();
  const proxyCode = String(toCode(params.proxy)).trim().toUpperCase();
  if (!assigneeCode && !proxyCode) return null;
  const excludeSet = new Set(
    (params.excludeIds || (params.excludeId ? [params.excludeId] : [])).map(String),
  );

  for (const r of list) {
    // 僅「啟用(1)」與「待生效(P)」視為有效占用
    const st = String(r.status);
    if (st !== "1" && st !== "P") continue;
    const rowIds = Array.isArray(r.ids) && r.ids.length
      ? r.ids.map(String)
      : [String(r.id)];
    if (rowIds.some((id) => excludeSet.has(id))) continue;
    const rowFormKeys = Array.isArray(r.formKeys) && r.formKeys.length
      ? r.formKeys
      : [r.formKey || DEFAULT_FORM_KEY];
    if (!rowFormKeys.some((fk) => formKeysConflict(fk, newFormKey))) continue;
    if (!datesOverlap(params.startDate, params.endDate, r.startDate, r.endDate)) continue;

    const rowAssignee = String(toCode(r.assignee)).trim().toUpperCase();
    const rowProxy = String(toCode(r.proxy)).trim().toUpperCase();

    let hitCode = "";
    let hitLabel = "";
    let reason = "";
    // 同一被代理人不可重疊期間重複設定
    if (assigneeCode && assigneeCode === rowAssignee) {
      hitCode = assigneeCode;
      hitLabel = userLabel(params.assignee);
      reason = "已是被代理人，不能重複設定";
    // 代理鏈：角色交錯；同一代理人可代理多人，不擋
    } else if (assigneeCode && assigneeCode === rowProxy) {
      hitCode = assigneeCode;
      hitLabel = userLabel(params.assignee);
      reason = "已是代理人，不能再設為被代理人（會形成代理鏈）";
    } else if (proxyCode && proxyCode === rowAssignee) {
      hitCode = proxyCode;
      hitLabel = userLabel(params.proxy);
      reason = "已是被代理人，不能再設為代理人（會形成代理鏈）";
    }
    if (hitCode) {
      const range = overlapRange(params.startDate, params.endDate, r.startDate, r.endDate);
      return {
        code: hitCode,
        name: hitLabel,
        reason,
        overlapStart: range?.start,
        overlapEnd: range?.end,
      };
    }
  }
  return null;
};

const findProxyPersonConflict = async (excludeIds?: string[]) => {
  const list = await fetchAllProxySettings();
  const keys = resolvedFormKeys();
  const base = {
    assignee: isAdmin.value ? form.assignee : currentUserPickerValue.value,
    proxy: form.proxy,
    startDate: toWesternStorageDate(form.startDate),
    endDate: toWesternStorageDate(form.endDate),
    excludeIds:
      excludeIds
      ?? (dialogStatus.value === "edit"
        ? (editingGroupIds.value.length ? editingGroupIds.value : [form.id])
        : undefined),
  };
  for (const fk of keys) {
    const hit = findProxyPersonConflictInList(list, { ...base, formKey: fk });
    if (hit) return hit;
  }
  return null;
};

const findProxyPersonConflictForRow = async (row: any) => {
  const list = await fetchAllProxySettings();
  const keys = row.formKeys || [row.formKey ?? DEFAULT_FORM_KEY];
  const excludeIds = rowGroupIds(row);
  for (const fk of keys) {
    const hit = findProxyPersonConflictInList(list, {
      formKey: fk,
      assignee: row.assignee ?? "",
      proxy: row.proxy ?? "",
      startDate: toWesternStorageDate(row.startDate),
      endDate: toWesternStorageDate(row.endDate),
      excludeIds,
    });
    if (hit) return hit;
  }
  return null;
};

// 依目標狀態呼叫對應的 enable/disable API。
// 啟用時是否轉移待辦，由 dialog 內的勾選框（transferTasks）決定。
// 同組多筆僅最後一筆 notify=true，避免重複寄信與操作紀錄。
const applyStatusChange = async (id: string, target: string, notify = true) => {
  if (target === "1") {
    await $api(`${assigneeProxy.enable}/${id}`, {
      method: "POST",
      query: {
        transferTasks: transferTasks.value,
        systemId: resolveMailSystemId(),
        notify,
      },
    });
    return true;
  }
  if (target === "2") {
    await $api(`${assigneeProxy.disable}/${id}`, {
      method: "POST",
      query: { systemId: resolveMailSystemId(), notify },
    });
    return true;
  }
  return false;
};

const applyStatusChangeBatch = async (ids: string[], target: string) => {
  for (let i = 0; i < ids.length; i++) {
    await applyStatusChange(String(ids[i]), target, i === ids.length - 1);
  }
};

const save = async () => {
  const { valid } = await formRef.value.validate();
  if (!valid) return;
  if (formScopeMode.value === "specific" && resolvedFormKeys().length === 0) {
    popSwal("warning", "無法儲存", "請至少選擇一個指定代理範圍");
    return;
  }

  submitting.value = true;
  try {
    // 新增／修改固定啟用（開始日未到則後端存待生效）
    form.status = "1";
    const conflict = await findProxyPersonConflict();
    if (conflict) {
      popSwal("warning", "無法儲存", PROXY_CONFLICT_MSG(conflict.name, conflict.reason, conflict.overlapStart, conflict.overlapEnd));
      return;
    }

    const extractIds = (resp: any): string[] => {
      if (Array.isArray(resp?.ids) && resp.ids.length) return resp.ids.map(String);
      const id = resp?.id ?? resp?.data?.id;
      return id ? [String(id)] : [];
    };

    if (dialogStatus.value === "add") {
      const resp: any = await $api(assigneeProxy.create, { method: "POST", body: buildPayload() });
      const newIds = extractIds(resp);
      await applyStatusChangeBatch(newIds, "1");
      popSwal("success", newIds.length > 1 ? `新增成功（${newIds.length} 筆代理範圍）` : "新增成功");
    } else if (isEnabledReplaceEdit.value) {
      // 啟用中不可原地 update：先停用舊組 → 新增 → 再啟用
      if (originalAssignee.value) {
        form.assignee = toPickerValue(originalAssignee.value);
      }
      const oldIds = editingGroupIds.value.length ? editingGroupIds.value : [form.id];
      await applyStatusChangeBatch(oldIds.map(String), "2");
      let newIds: string[] = [];
      try {
        const resp: any = await $api(assigneeProxy.create, { method: "POST", body: buildPayload() });
        newIds = extractIds(resp);
      } catch (error) {
        popSwal("error", "原設定已停用，但新設定建立失敗，請重新新增");
        console.error("啟用中修改：建立新設定失敗:", error);
        isDialogVisible.value = false;
        fetchData();
        return;
      }
      if (!newIds.length) {
        popSwal("error", "原設定已停用，但未取得新設定編號，請重新新增後手動啟用");
        isDialogVisible.value = false;
        fetchData();
        return;
      }
      try {
        await applyStatusChangeBatch(newIds, "1");
      } catch (error) {
        popSwal("error", "新設定已建立但啟用失敗，請至清單手動啟用");
        console.error("啟用中修改：啟用新設定失敗:", error);
        isDialogVisible.value = false;
        fetchData();
        return;
      }
      popSwal("success", newIds.length > 1 ? `修改成功（${newIds.length} 筆代理範圍）` : "修改成功");
    } else {
      // 未啟用(0)／待生效(P)：同組 sync formKeys 後固定啟用
      const body = buildPayload();
      const resp: any = await $api(`${assigneeProxy.update}/${form.id}`, { method: "PUT", body });
      const ids = extractIds(resp);
      const targetIds = ids.length ? ids : (editingGroupIds.value.length ? editingGroupIds.value : [form.id]);
      if (String(originalStatus.value) !== "1") {
        await applyStatusChangeBatch(targetIds.map(String), "1");
      }
      popSwal("success", targetIds.length > 1 ? `修改成功（${targetIds.length} 筆代理範圍）` : "修改成功");
    }
    isDialogVisible.value = false;
    fetchData();
  } catch (error) {
    popSwal("error", "儲存失敗，請稍後再試");
    console.error("儲存代理人設定失敗:", error);
  } finally {
    submitting.value = false;
  }
};

const enableBtn = async (row: any) => {
  if (!row) return;
  const conflict = await findProxyPersonConflictForRow(row);
  if (conflict) {
    popSwal("warning", "無法啟用", PROXY_CONFLICT_MSG(conflict.name, conflict.reason, conflict.overlapStart, conflict.overlapEnd));
    return;
  }
  const ids = rowGroupIds(row);
  const countHint = ids.length > 1 ? `（共 ${ids.length} 筆代理範圍）` : "";
  $swal({
    title: "啟用代理人設定",
    text: `是否同時將被代理人現有的待辦一併轉交給代理人？${countHint}`,
    icon: "question",
    showDenyButton: true,
    showCancelButton: true,
    confirmButtonText: "啟用並轉移待辦",
    denyButtonText: "僅啟用",
    cancelButtonText: "取消",
  }).then(async (result: any) => {
    if (result.isDismissed) return;
    const transfer = result.isConfirmed;
    try {
      for (let i = 0; i < ids.length; i++) {
        await $api(`${assigneeProxy.enable}/${ids[i]}`, {
          method: "POST",
          query: {
            transferTasks: transfer,
            systemId: resolveMailSystemId(),
            notify: i === ids.length - 1,
          },
        });
      }
      popSwal("success", ids.length > 1 ? `啟用成功（${ids.length} 筆）` : "啟用成功");
      fetchData();
    } catch (error) {
      popSwal("error", "啟用失敗，請稍後再試");
      console.error("啟用代理人設定失敗:", error);
    }
  });
};

const disableBtn = (row: any) => {
  if (!row) return;
  const ids = rowGroupIds(row);
  const countHint = ids.length > 1 ? `將停用 ${ids.length} 筆代理範圍設定。` : "";
  $swal({
    title: "停用代理人設定",
    text: `${countHint}確定要停用此代理人設定嗎？停用後狀態將顯示為「已結束」。`,
    showCancelButton: true,
    confirmButtonText: "我要停用",
    cancelButtonText: "取消",
  }).then(async (result: any) => {
    if (!result.isConfirmed) return;
    disablingId.value = String(ids[0]);
    try {
      for (let i = 0; i < ids.length; i++) {
        await $api(`${assigneeProxy.disable}/${ids[i]}`, {
          method: "POST",
          query: {
            systemId: resolveMailSystemId(),
            notify: i === ids.length - 1,
          },
        });
      }
      popSwal("success", ids.length > 1 ? `停用成功（${ids.length} 筆）` : "停用成功");
      fetchData();
    } catch (error) {
      popSwal("error", "停用失敗，請稍後再試");
      console.error("停用代理人設定失敗:", error);
    } finally {
      disablingId.value = null;
    }
  });
};

const deleteBtn = (row: any) => {
  if (!row) return;
  const st = String(row.status);
  if (st !== "0" && st !== "P") {
    popSwal("warning", "無法刪除", "只有未啟用或待生效的代理人設定才能刪除");
    return;
  }
  const ids = rowGroupIds(row);
  const countHint = ids.length > 1 ? `將刪除 ${ids.length} 筆代理範圍設定。` : "";
  $swal({
    title: "刪除資料",
    text: `${countHint}確定要刪除此代理人設定嗎？`,
    showCancelButton: true,
    confirmButtonText: "我要刪除",
    cancelButtonText: "取消",
  }).then(async (result: any) => {
    if (!result.isConfirmed) return;
    try {
      for (let i = 0; i < ids.length; i++) {
        await $api(`${assigneeProxy.delete}/${ids[i]}`, {
          method: "DELETE",
          // 第一筆寫彙總 log（此時同組仍齊全）；其餘不寫
          query: { writeLog: i === 0 },
        });
      }
      popSwal("success", ids.length > 1 ? `刪除成功（${ids.length} 筆）` : "刪除成功");
      fetchData();
    } catch (error) {
      popSwal("error", "刪除失敗，請稍後再試");
      console.error("刪除代理人設定失敗:", error);
    }
  });
};

watch(size, () => {
  if (!smAndDown.value) fetchData();
});
watch(page, () => {
  if (!smAndDown.value) fetchData();
});

/** 切換手機／桌面時重設載入策略 */
watch(smAndDown, (mobile) => {
  page.value = 1;
  mobileNextPageNo.value = 0;
  if (!mobile) {
    size.value = DESKTOP_PAGE_SIZE;
  }
  fetchData();
});

let fetchDataTimer: ReturnType<typeof setTimeout> | null = null;
const scheduleFetchData = () => {
  if (fetchDataTimer)
    clearTimeout(fetchDataTimer);
  fetchDataTimer = setTimeout(() => {
    fetchDataTimer = null;
    page.value = 1;
    mobileNextPageNo.value = 0;
    fetchData();
  }, 300);
};
watch(filters, scheduleFetchData, { deep: true });

onMounted(async () => {
  if (smAndDown.value) {
    size.value = MOBILE_PAGE_SIZE;
  }
  await Promise.all([fetchConfig(), fetchFormKeyOptions()]);
  fetchData();
});
</script>

<style scoped>
.rounded-pill {
  border-radius: 50px !important;
}

.bg-surface {
  background-color: rgb(var(--v-theme-surface));
}

.list-section-title {
  letter-spacing: 0.02em;
}

/* 狀態搜尋：僅上色，維持原本 compact 高度 */
.search-filters-row .search-filter-status .v-chip {
  font-weight: 600 !important;
}

.search-filters-row .search-filter-formkey .v-chip {
  font-weight: 600 !important;
}

/* ===== 卡片式代理人設定清單 ===== */
.proxy-list-wrap {
  min-height: 200px;
}

.proxy-card-list {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.proxy-card {
  position: relative;
  display: flex;
  overflow: hidden;
  border-radius: 14px;
  border: 1px solid rgba(var(--v-theme-on-surface), 0.08);
  background: rgb(var(--v-theme-surface));
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  transition:
    box-shadow 0.2s ease,
    border-color 0.2s ease;
}

.proxy-card:hover {
  border-color: rgba(var(--v-theme-primary), 0.22);
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.07);
}

.proxy-card__accent {
  width: 5px;
  flex-shrink: 0;
  background: rgba(var(--v-theme-on-surface), 0.18);
}

.proxy-card--status-1 .proxy-card__accent {
  background: rgb(var(--v-theme-success));
}

.proxy-card--status-P .proxy-card__accent {
  background: rgb(var(--v-theme-info));
}

.proxy-card--status-0 .proxy-card__accent {
  background: rgb(var(--v-theme-warning));
}

.proxy-card--status-2 .proxy-card__accent {
  background: rgba(var(--v-theme-on-surface), 0.28);
}

.proxy-card__main {
  flex: 1;
  min-width: 0;
  padding: 16px 18px 14px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.proxy-card__top {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
}

.proxy-card__status-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

.proxy-card__status-row .proxy-card__status-chip,
.proxy-card__status-row .proxy-card__period {
  height: 36px;
  min-height: 36px;
  padding: 0 14px;
  font-size: 1.0625rem;
  font-weight: 600;
  line-height: 1;
  box-sizing: border-box;
}

.proxy-card__status-row .proxy-card__status-chip :deep(.v-chip__content) {
  font-size: 1.0625rem;
  font-weight: 600;
}

.proxy-card__status-row .proxy-card__status-chip :deep(.v-icon) {
  font-size: 1.25rem !important;
  margin-inline-end: 4px;
}

.proxy-card__meta-time {
  font-size: 0.9375rem;
  color: rgb(var(--v-theme-on-surface));
}

.proxy-card__sync-fail {
  font-size: 0.9375rem;
  color: rgb(var(--v-theme-error));
  font-weight: 600;
}

.proxy-card__period {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border-radius: 999px;
  background: rgba(var(--v-theme-on-surface), 0.04);
  border: 1px solid rgba(var(--v-theme-on-surface), 0.06);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  color: rgb(var(--v-theme-on-surface));
}

.proxy-card__period-icon {
  color: rgb(var(--v-theme-primary));
  opacity: 0.85;
}

.proxy-card__period-sep {
  color: rgb(var(--v-theme-on-surface));
  font-weight: 500;
}

.proxy-card__people {
  display: flex;
  align-items: stretch;
  gap: 10px;
  flex-wrap: wrap;
}

.proxy-card__person {
  flex: 1 1 160px;
  min-width: 140px;
  padding: 10px 14px;
  border-radius: 12px;
  background: rgba(var(--v-theme-primary), 0.05);
  border: 1px solid rgba(var(--v-theme-primary), 0.1);
}

.proxy-card__person--proxy {
  background: rgba(var(--v-theme-warning), 0.07);
  border-color: rgba(var(--v-theme-warning), 0.18);
}

.proxy-card__person-role {
  display: block;
  font-size: 0.875rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  color: rgb(var(--v-theme-on-surface));
  margin-bottom: 4px;
}

.proxy-card__person-name {
  display: block;
  font-size: 1.1875rem;
  font-weight: 700;
  line-height: 1.4;
  word-break: break-word;
  color: rgb(var(--v-theme-on-surface));
}

.proxy-card__arrow {
  display: flex;
  align-items: center;
  color: rgb(var(--v-theme-on-surface));
  padding: 0 2px;
}

.proxy-card__scope {
  padding: 12px 14px;
  border-radius: 12px;
  background: rgba(var(--v-theme-on-surface), 0.025);
  border: 1px solid rgba(var(--v-theme-on-surface), 0.06);
}

.proxy-card__scope-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.9375rem;
  font-weight: 700;
  color: rgb(var(--v-theme-on-surface));
  margin-bottom: 10px;
}

.proxy-card__scope-count {
  margin-left: 2px;
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 0.8125rem;
  background: rgba(var(--v-theme-primary), 0.1);
  color: rgb(var(--v-theme-primary));
}

.proxy-card__scope-body {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.proxy-scope-system {
  padding: 10px 12px;
  border-radius: 10px;
  background: rgb(var(--v-theme-surface));
  border: 1px solid rgba(var(--v-theme-on-surface), 0.08);
}

.proxy-scope-system__head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.proxy-scope-system__name {
  font-size: 0.9375rem;
  font-weight: 700;
  color: rgb(var(--v-theme-on-surface));
}

.proxy-scope-system__count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.5rem;
  height: 1.5rem;
  padding: 0 7px;
  border-radius: 999px;
  font-size: 0.9375rem;
  font-weight: 700;
  color: rgb(var(--v-theme-on-surface));
  background: rgba(var(--v-theme-on-surface), 0.08);
}

.proxy-scope-system--1 {
  border-color: rgba(var(--v-theme-info), 0.22);
  background: rgba(var(--v-theme-info), 0.04);
}

.proxy-scope-system--2 {
  border-color: rgba(var(--v-theme-warning), 0.28);
  background: rgba(var(--v-theme-warning), 0.05);
}

.proxy-scope-system--3 {
  border-color: rgba(var(--v-theme-success), 0.22);
  background: rgba(var(--v-theme-success), 0.04);
}

.proxy-scope-system--0 {
  border-color: rgba(var(--v-theme-on-surface), 0.1);
  background: rgba(var(--v-theme-on-surface), 0.02);
}

.proxy-scope-system__tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.proxy-scope-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 14px;
  border-radius: 999px;
  font-size: 1.0625rem;
  font-weight: 600;
  line-height: 1.4;
  white-space: nowrap;
}

.proxy-scope-badge--default {
  color: rgb(var(--v-theme-on-surface));
  background: rgba(var(--v-theme-on-surface), 0.06);
  border: 1px solid rgba(var(--v-theme-on-surface), 0.08);
}

.proxy-scope-tag {
  display: inline-block;
  max-width: 100%;
  padding: 6px 12px;
  border-radius: 8px;
  font-size: 0.9375rem;
  font-weight: 600;
  line-height: 1.45;
  color: rgb(var(--v-theme-on-surface));
  background: rgb(var(--v-theme-surface));
  border: 1px solid rgba(var(--v-theme-on-surface), 0.1);
  word-break: break-word;
}

.proxy-card__remark {
  padding: 12px 14px;
  border-radius: 12px;
  background: rgba(var(--v-theme-on-surface), 0.025);
  border: 1px solid rgba(var(--v-theme-on-surface), 0.06);
}

.proxy-card__remark-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.9375rem;
  font-weight: 700;
  color: rgb(var(--v-theme-on-surface));
  margin-bottom: 8px;
}

.proxy-card__remark-body {
  font-size: 0.9375rem;
  font-weight: 500;
  line-height: 1.55;
  color: rgb(var(--v-theme-on-surface));
  white-space: pre-wrap;
  word-break: break-word;
}

.proxy-card__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  padding-top: 4px;
  border-top: 1px solid rgba(var(--v-theme-on-surface), 0.06);
}

.proxy-card__audit {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
  flex: 1 1 220px;
}

.proxy-card__audit-row {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  column-gap: 10px;
  align-items: start;
}

.proxy-card__audit-label {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 2.75rem;
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 0.8125rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  line-height: 1.4;
  color: rgb(var(--v-theme-primary));
  background: rgba(var(--v-theme-primary), 0.1);
}

.proxy-card__audit-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  column-gap: 8px;
  row-gap: 2px;
  min-width: 0;
}

.proxy-card__audit-user {
  font-size: 0.875rem;
  font-weight: 600;
  line-height: 1.4;
  color: rgb(var(--v-theme-on-surface));
  word-break: break-word;
}

.proxy-card__audit-time {
  font-size: 0.8125rem;
  font-weight: 500;
  line-height: 1.4;
  color: rgba(var(--v-theme-on-surface), 0.58);
  white-space: nowrap;
}

.proxy-card__actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-left: auto;
}

/* ===== 新增／修改 dialog ===== */
.proxy-form-card {
  overflow: hidden;
  border: 1px solid rgba(var(--v-theme-on-surface), 0.08);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.12);
}

.proxy-form-card__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 18px 20px 14px;
  border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.08);
  background:
    linear-gradient(
      180deg,
      rgba(var(--v-theme-primary), 0.06) 0%,
      rgba(var(--v-theme-surface), 0) 100%
    );
}

.proxy-form-card__header-main {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  min-width: 0;
}

.proxy-form-card__header-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  border-radius: 12px;
  color: rgb(var(--v-theme-primary));
  background: rgba(var(--v-theme-primary), 0.12);
}

.proxy-form-card__header-text {
  min-width: 0;
}

.proxy-form-card__title {
  font-size: 1.125rem;
  font-weight: 700;
  line-height: 1.35;
  color: rgb(var(--v-theme-on-surface));
}

.proxy-form-card__subtitle {
  margin-top: 2px;
  font-size: 0.8125rem;
  line-height: 1.4;
  color: rgba(var(--v-theme-on-surface), 0.62);
}

.proxy-form-card__body {
  padding: 16px 20px 8px !important;
  max-height: min(68vh, 640px);
}

.proxy-form {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.proxy-form-section {
  padding: 14px 14px 12px;
  border-radius: 12px;
  border: 1px solid rgba(var(--v-theme-on-surface), 0.08);
  background: rgba(var(--v-theme-on-surface), 0.015);
}

.proxy-form-section--last {
  margin-bottom: 4px;
}

.proxy-form-section__head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  font-size: 0.9375rem;
  font-weight: 700;
  color: rgb(var(--v-theme-on-surface));
}

.proxy-form-section__req {
  margin-left: auto;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
  color: rgb(var(--v-theme-error));
  background: rgba(var(--v-theme-error), 0.08);
}

.proxy-form-label {
  display: inline-flex;
  align-items: baseline;
  gap: 4px;
  margin-bottom: 6px;
  font-size: 0.875rem;
  font-weight: 600;
  color: rgb(var(--v-theme-on-surface));
}

.proxy-form-label__optional {
  font-size: 0.75rem;
  font-weight: 400;
  color: rgba(var(--v-theme-on-surface), 0.55);
}

.proxy-form-hint {
  display: block;
  margin-top: 4px;
  font-size: 0.75rem;
  color: rgba(var(--v-theme-on-surface), 0.55);
}

.proxy-form-scope-toggle {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

.proxy-form-scope-option {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  width: 100%;
  padding: 12px 12px 11px;
  border-radius: 10px;
  border: 1px solid rgba(var(--v-theme-on-surface), 0.12);
  background: rgb(var(--v-theme-surface));
  color: rgb(var(--v-theme-on-surface));
  text-align: left;
  cursor: pointer;
  transition:
    border-color 0.15s ease,
    background-color 0.15s ease,
    box-shadow 0.15s ease;
}

.proxy-form-scope-option:hover {
  border-color: rgba(var(--v-theme-primary), 0.35);
}

.proxy-form-scope-option--active {
  border-color: rgba(var(--v-theme-primary), 0.55);
  background: rgba(var(--v-theme-primary), 0.08);
  box-shadow: inset 0 0 0 1px rgba(var(--v-theme-primary), 0.2);
}

.proxy-form-scope-option--active .v-icon {
  color: rgb(var(--v-theme-primary));
}

.proxy-form-scope-option__text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.proxy-form-scope-option__title {
  font-size: 0.9375rem;
  font-weight: 700;
  line-height: 1.3;
}

.proxy-form-scope-option__desc {
  font-size: 0.75rem;
  line-height: 1.35;
  color: rgba(var(--v-theme-on-surface), 0.58);
}

.proxy-form-scope-picker {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.proxy-form-scope-picker__list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-height: min(42vh, 360px);
  overflow: auto;
  padding: 8px;
  border-radius: 10px;
  border: 1px solid rgba(var(--v-theme-on-surface), 0.1);
  background: rgb(var(--v-theme-surface));
}

.proxy-form-scope-picker__empty,
.proxy-form-scope-picker__hint {
  font-size: 0.8125rem;
  color: rgba(var(--v-theme-on-surface), 0.62);
}

.proxy-form-scope-group {
  border-radius: 10px;
  border: 1px solid rgba(var(--v-theme-on-surface), 0.1);
  background: rgba(var(--v-theme-on-surface), 0.02);
}

.proxy-form-scope-group__head {
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 2px 6px 2px 4px;
  background: rgba(var(--v-theme-primary), 0.07);
  border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.06);
}

.proxy-form-scope-group--collapsed .proxy-form-scope-group__head {
  border-bottom: none;
}

.proxy-form-scope-group__check {
  flex-shrink: 0;
}

.proxy-form-scope-group__toggle {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
  padding: 6px 4px;
  border: 0;
  background: transparent;
  cursor: pointer;
  text-align: left;
  color: inherit;
}

.proxy-form-scope-group__title {
  flex: 1;
  min-width: 0;
  font-size: 0.9375rem;
  font-weight: 700;
  line-height: 1.3;
  color: rgb(var(--v-theme-on-surface));
}

.proxy-form-scope-group__total {
  font-weight: 600;
  color: rgba(var(--v-theme-on-surface), 0.55);
}

.proxy-form-scope-group__count {
  flex-shrink: 0;
  font-size: 0.75rem;
  font-weight: 600;
  color: rgba(var(--v-theme-on-surface), 0.55);
  white-space: nowrap;
}

.proxy-form-scope-group__chevron {
  flex-shrink: 0;
  color: rgba(var(--v-theme-on-surface), 0.45);
}

.proxy-form-scope-group__body {
  display: grid;
  grid-template-columns: 1fr;
  gap: 4px;
  padding: 8px 10px 10px;
}

@media (min-width: 600px) {
  .proxy-form-scope-group__body {
    grid-template-columns: 1fr 1fr;
  }
}

.proxy-form-scope-item {
  display: flex;
  align-items: flex-start;
  gap: 2px;
  min-width: 0;
  margin: 0;
  cursor: pointer;
}

.proxy-form-scope-item__text {
  padding-top: 6px;
  font-size: 0.875rem;
  line-height: 1.35;
  color: rgb(var(--v-theme-on-surface));
  word-break: break-word;
}

.proxy-form-people {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  gap: 10px;
  align-items: start;
}

.proxy-form-people__arrow {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 56px;
  margin-top: 28px;
  color: rgba(var(--v-theme-on-surface), 0.35);
}

.proxy-form-status-hint {
  display: inline-flex;
  align-items: flex-start;
  gap: 6px;
  margin-top: 10px;
  padding: 8px 10px;
  border-radius: 8px;
  font-size: 0.8125rem;
  line-height: 1.4;
  color: rgba(var(--v-theme-on-surface), 0.72);
  background: rgba(var(--v-theme-info), 0.08);
}

.proxy-form-option-box {
  padding: 4px 10px;
  border-radius: 10px;
  border: 1px solid rgba(var(--v-theme-on-surface), 0.08);
  background: rgb(var(--v-theme-surface));
}

.proxy-form-card__footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 12px 20px 16px;
  border-top: 1px solid rgba(var(--v-theme-on-surface), 0.08);
  background: rgba(var(--v-theme-on-surface), 0.02);
}

@media (max-width: 600px) {
  .proxy-form-scope-toggle {
    grid-template-columns: 1fr;
  }

  .proxy-form-people {
    grid-template-columns: 1fr;
    gap: 8px;
  }

  .proxy-form-people__arrow {
    height: auto;
    margin: 0;
    transform: rotate(90deg);
  }

  .proxy-form-card__body {
    max-height: min(72vh, 560px);
  }
}

.proxy-list-pagination {
  border-top: 1px solid rgba(var(--v-theme-on-surface), 0.06);
  padding-top: 8px;
}

@media (max-width: 600px) {
  .proxy-card__arrow {
    display: none;
  }

  .proxy-card__main {
    padding: 14px 14px 12px;
  }

  .proxy-card__footer {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }

  .proxy-card__audit {
    flex: none;
    width: 100%;
    padding: 10px 12px;
    border-radius: 10px;
    background: rgba(var(--v-theme-on-surface), 0.03);
    gap: 8px;
  }

  .proxy-card__audit-row {
    grid-template-columns: auto minmax(0, 1fr);
    column-gap: 10px;
  }

  .proxy-card__audit-meta {
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
  }

  .proxy-card__audit-time {
    white-space: normal;
  }

  .proxy-card__actions {
    width: 100%;
    margin-left: 0;
  }

  .proxy-card__actions .v-btn {
    flex: 1 1 auto;
  }
}

/* ===== 桌面表格美化（操作紀錄 rich-data-table）===== */
.rich-data-table {
  border-radius: 12px !important;
  overflow: hidden;
}

.log-scope {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-width: 300px;
  padding: 2px 0;
  white-space: normal;
  line-height: 1.45;
  font-size: 0.9375rem;
}

.log-scope__default {
  font-size: 0.9375rem;
  color: rgba(var(--v-theme-on-surface), 0.7);
}

.log-scope__summary {
  font-size: 0.875rem;
  font-weight: 600;
  color: rgba(var(--v-theme-on-surface), 0.65);
}

.log-scope__hint {
  font-size: 0.875rem;
  color: rgba(var(--v-theme-on-surface), 0.5);
}

.log-scope__sys {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.log-scope__sys-name {
  font-size: 0.875rem;
  font-weight: 700;
  color: rgba(var(--v-theme-on-surface), 0.72);
}

.log-scope__sys--1 .log-scope__sys-name {
  color: rgb(var(--v-theme-info));
}

.log-scope__sys--2 .log-scope__sys-name {
  color: rgb(var(--v-theme-warning));
}

.log-scope__sys--3 .log-scope__sys-name {
  color: rgb(var(--v-theme-success));
}

.log-scope__sys--0 .log-scope__sys-name {
  color: rgba(var(--v-theme-on-surface), 0.72);
}

.log-scope__tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.log-scope__tag {
  display: inline-block;
  max-width: 100%;
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 0.9375rem;
  line-height: 1.5;
  background: rgba(var(--v-theme-on-surface), 0.06);
  color: rgb(var(--v-theme-on-surface));
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* 搜尋條件欄位：對齊請銷假清單，鎖住實際高度並讓 icon / 文字垂直置中。 */
.search-filters-row {
  --search-field-height: 44px;
  --search-field-font-size: 1rem;
}

.proxy-remark-preview {
  cursor: pointer;
  border-bottom: 1px dashed rgba(var(--v-border-color), 0.5);
  white-space: nowrap;
}

/* 檢視切換（代理人設定 / 操作紀錄）— 比照請銷假清單之分段切換 */
.cancellation-toggle {
  position: relative;
  display: inline-flex;
  align-items: stretch;
  padding: 4px;
  height: 42px;
  border-radius: 999px;
  background: rgba(var(--v-theme-on-surface), 0.06);
  border: 1px solid rgba(var(--v-theme-on-surface), 0.08);
  box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.05);
  user-select: none;
  isolation: isolate;
}

.cancellation-toggle__slider {
  position: absolute;
  top: 4px;
  bottom: 4px;
  left: 4px;
  width: calc((100% - 8px) / 3);
  border-radius: 999px;
  background: linear-gradient(135deg,
    rgb(var(--v-theme-primary)) 0%,
    color-mix(in srgb, rgb(var(--v-theme-primary)) 85%, #fff) 100%);
  box-shadow:
    0 2px 8px rgba(var(--v-theme-primary), 0.4),
    0 1px 2px rgba(0, 0, 0, 0.08);
  transition: transform 0.4s cubic-bezier(0.22, 1, 0.36, 1);
  z-index: 0;
  pointer-events: none;
}

/* 兩段式：滑塊寬度改為 1/2 */
.cancellation-toggle--two .cancellation-toggle__slider {
  width: calc((100% - 8px) / 2);
}

.cancellation-toggle__btn {
  position: relative;
  z-index: 1;
  flex: 1 1 0;
  min-width: 110px;
  height: 34px;
  padding: 0 16px;
  border: none;
  background: transparent;
  border-radius: 999px;
  color: rgba(var(--v-theme-on-surface), 0.62);
  font-size: 0.95rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
  outline: none;
  transition:
    color 0.3s cubic-bezier(0.22, 1, 0.36, 1),
    transform 0.3s cubic-bezier(0.22, 1, 0.36, 1);
  white-space: nowrap;
}
.cancellation-toggle__btn:hover:not(.is-active) {
  color: rgba(var(--v-theme-on-surface), 0.88);
}
.cancellation-toggle__btn:active:not(.is-active) {
  transform: scale(0.96);
}
.cancellation-toggle__btn.is-active {
  color: #fff;
}
.cancellation-toggle__btn .v-icon {
  font-size: 1.1rem !important;
  transition: transform 0.3s cubic-bezier(0.22, 1, 0.36, 1);
}
.cancellation-toggle__btn.is-active .v-icon {
  transform: scale(1.08);
}
.cancellation-toggle__btn:focus-visible {
  box-shadow: 0 0 0 2px rgba(var(--v-theme-primary), 0.35);
}
</style>

<!-- 備註 tooltip 內容會 teleport 到 body，需用全域 class（比照結訓單簽章報告表）-->
<style>
.rich-data-table .v-table__wrapper {
  border-radius: 12px;
}

.rich-data-table thead th {
  background-color: rgb(var(--v-theme-surface)) !important;
  background-image: linear-gradient(
    180deg,
    rgba(var(--v-theme-primary), 0.14) 0%,
    rgba(var(--v-theme-primary), 0.06) 100%
  ) !important;
  color: rgb(var(--v-theme-on-surface)) !important;
  font-weight: 600 !important;
  font-size: 1rem !important;
  text-transform: none !important;
  letter-spacing: 0.02em;
  border-bottom: 2px solid rgba(var(--v-theme-primary), 0.30) !important;
  padding: 14px 18px !important;
  height: 56px !important;
  white-space: nowrap;
  user-select: none;
}

.rich-data-table thead th .v-data-table-header__content {
  font-weight: 600;
  font-size: 1rem;
}

.rich-data-table tbody tr {
  transition: background-color 0.2s ease;
}

.rich-data-table tbody tr:nth-child(even) > td {
  background-color: rgba(var(--v-theme-on-surface), 0.018);
}

.rich-data-table tbody tr:hover > td {
  background-color: rgba(var(--v-theme-primary), 0.06) !important;
}

.rich-data-table tbody td {
  padding: 12px 18px !important;
  border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.06) !important;
  font-size: 0.975rem;
  vertical-align: middle;
}

.rich-data-table tbody td .v-chip {
  font-weight: 700 !important;
}

.rich-data-table tbody td .v-chip.v-chip--variant-flat {
  background-image: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.22) 0%,
    rgba(0, 0, 0, 0.18) 100%
  ) !important;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15) !important;
}

.search-filters-row .v-input,
.search-filters-row .v-input__control,
.search-filters-row .v-field {
  min-height: var(--search-field-height);
}

.search-filters-row .v-field {
  --v-input-control-height: var(--search-field-height);
  --v-field-input-padding-top: 0px;
  --v-field-input-padding-bottom: 0px;
  align-items: center;
  font-size: var(--search-field-font-size);
}

.search-filters-row .v-field__field,
.search-filters-row .v-field__input {
  min-height: var(--search-field-height);
  height: var(--search-field-height);
  display: flex;
  align-items: center;
}

.search-filters-row .v-field__input {
  padding-top: 0 !important;
  padding-bottom: 0 !important;
}

.search-filters-row .v-field__prepend-inner,
.search-filters-row .v-field__append-inner {
  align-self: stretch;
  display: inline-flex;
  align-items: center;
  padding-top: 0 !important;
  padding-bottom: 0 !important;
}

.search-filters-row .v-field__prepend-inner .v-icon,
.search-filters-row .v-field__append-inner .v-icon {
  font-size: 1.3rem;
  line-height: 1;
  opacity: 0.75;
}

.search-filters-row input::placeholder,
.search-filters-row .v-field input,
.search-filters-row .v-select__selection-text,
.search-filters-row .v-autocomplete input,
.search-filters-row .v-combobox input {
  font-size: var(--search-field-font-size);
  font-weight: 500;
  line-height: 1.4;
}

.search-filters-row input::placeholder {
  font-weight: 400;
  opacity: 0.8;
}

.search-filters-row .v-field:not(.v-field--active) .v-label.v-field-label {
  top: 50% !important;
  transform: translateY(-50%) !important;
  font-size: var(--search-field-font-size);
  line-height: 1.4;
}

.search-filters-row .v-field.v-field--active .v-label.v-field-label--floating {
  top: 0 !important;
  transform: translateY(-50%) !important;
}

.search-filters-row .v-select__selection,
.search-filters-row .v-autocomplete__selection,
.search-filters-row .v-combobox__selection {
  display: inline-flex;
  align-items: center;
  margin-top: 0 !important;
  margin-bottom: 0 !important;
  min-height: var(--search-field-height);
}

.search-filters-row .v-select__selection > *,
.search-filters-row .v-autocomplete__selection > *,
.search-filters-row .v-combobox__selection > * {
  display: inline-flex;
  align-items: center;
}

.search-filters-row .search-filter-control .v-field {
  height: var(--search-field-height) !important;
  min-height: var(--search-field-height) !important;
}

.search-filters-row .search-filter-control .v-field__prepend-inner,
.search-filters-row .search-filter-control .v-field__append-inner {
  height: var(--search-field-height) !important;
  min-height: var(--search-field-height) !important;
  align-self: stretch !important;
  display: inline-flex !important;
  align-items: center !important;
  padding-top: 0 !important;
  padding-bottom: 0 !important;
}

.search-filters-row .search-filter-control .v-field__prepend-inner > .v-icon,
.search-filters-row .search-filter-control .v-field__append-inner > .v-icon {
  margin-top: 0 !important;
  margin-bottom: 0 !important;
}

.search-filters-row .search-filter-control .v-field__field,
.search-filters-row .search-filter-control .v-field__input {
  height: var(--search-field-height) !important;
  min-height: var(--search-field-height) !important;
  display: flex !important;
  align-items: center !important;
}

.search-filters-row .search-filter-control .v-field__input {
  padding-top: 0 !important;
  padding-bottom: 0 !important;
  flex-wrap: nowrap !important;
}

.search-filters-row .search-filter-control .v-select__selection {
  height: var(--search-field-height) !important;
  min-height: var(--search-field-height) !important;
  display: inline-flex !important;
  align-items: center !important;
  margin: 0 !important;
}

.search-filters-row .search-filter-control .v-select__selection-text {
  display: inline-flex !important;
  align-items: center !important;
  line-height: 1 !important;
}

.search-filters-row .search-filter-control input {
  align-self: center !important;
  padding-top: 0 !important;
  padding-bottom: 0 !important;
  margin-top: 0 !important;
}

.search-filters-row .v-field__outline {
  --v-field-border-opacity: 0.22;
}

.proxy-remark-tooltip-shell {
  background: transparent !important;
  box-shadow: none !important;
  padding: 0 !important;
  opacity: 1 !important;
}

.proxy-remark-tooltip-card {
  display: flex;
  flex-direction: column;
  background: rgb(var(--v-theme-surface));
  color: rgb(var(--v-theme-on-surface));
  border: 1px solid rgba(var(--v-border-color), 0.14);
  border-radius: 10px;
  overflow: hidden;
  max-height: min(60vh, 420px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18);
}

.proxy-remark-tooltip-header {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: rgba(var(--v-theme-primary), 0.1);
  border-bottom: 1px solid rgba(var(--v-border-color), 0.14);
}

.proxy-remark-tooltip-title {
  font-size: 0.875rem;
  font-weight: 600;
  letter-spacing: 0.02em;
}

.proxy-remark-tooltip-body {
  flex: 1 1 auto;
  min-height: 0;
  padding: 12px 14px;
  white-space: pre-wrap;
  word-break: break-word;
  line-height: 1.55;
  font-size: 0.875rem;
  overflow-y: auto;
  overscroll-behavior: contain;
}
</style>
